import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { WebsiteType } from 'modules/Const'
import useWebsiteBuilder, {
    WebsiteStatus,
    WebsiteBuilderContent,
    WebsiteBuilderContentType,
    PresavePlatform,
    PresaveWebsiteBuilderStyle,
    TourStyle,
    StreamingPlatformsStyle,
    CollectDataInputType,
    WebsiteTextAlign
} from 'Hooks/useWebsiteBuilder'
import useBrand from 'Hooks/useBrand'
import { STREAMING_PLATFORMS_WEBSITE_CONTENT } from 'Hooks/useWebsiteBuilder'
import { WebsiteBuilderDataPayload } from 'api/websiteBuilderApi'
import axios from 'axios'
import { CloudinaryUploadedFileType } from 'components/CampaignViews/CloudinaryUploadWidget'
import Axios from 'helpers/Interceptor'
import { getMarketingPlan_api } from 'api/marketingPlanApi'
import {
    shouldShowToolCall,
    shouldShowInSystemMessages,
    getToolCallStartMessage,
    getToolCallSuccessMessage,
    getToolCallErrorMessage
} from 'components/Assistant/toolCallConfig'

// Tasks & Chats TypeScript Interfaces
interface CampaignTaskMetadata {
    goalMetadata: any;
    platformReferences: any;
    strategyId: string | null;
    playbookType?: string;
}

interface AssetTaskMetadata {
    generationParameters: any;
    resultCount: number;
    hasErrors: boolean;
    assetType?: 'video' | 'image';
}

interface ContentAnalysisTaskMetadata {
    analysisType: string;
    videosAnalyzed: number;
    recommendations: number;
    hasExpired: boolean;
    expiresAt?: string;
}

interface MarketingPlanTaskMetadata {
    planType: string;
    objective: string;
    actionsCount: number;
    actionsByStatus: {
        pending: number;
        in_progress: number;
        completed: number;
        cancelled: number;
    };
    budget: {
        total: number;
        totalFromActions: number;
        hasDetailedBudget: boolean;
    };
    completionRate: number;
}

export interface AgentJob {
    id: string;
    type: 'campaign' | 'asset' | 'content_analysis' | 'marketing_plan';
    status: 'draft' | 'generating' | 'completed' | 'failed' | 'DRAFT' | 'EXECUTING' | 'COMPLETED' | 'processing' | 'pending';
    title: string;
    description: string;
    progress: number; // 0-100
    chatId: string | null;
    conversationType: 'campaign_creation' | 'asset_generation' | 'general_chat' | 'mixed' | null;
    playbookType?: string; // For campaign jobs
    planType?: string; // For marketing plan jobs
    username?: string; // For content analysis jobs
    platform?: string; // For content analysis jobs 
    analysisId?: string; // For content analysis jobs
    createdAt: string;
    updatedAt: string;
    metadata: CampaignTaskMetadata | AssetTaskMetadata | ContentAnalysisTaskMetadata | MarketingPlanTaskMetadata;
}

export interface ChatConversation {
    id: string;
    status: 'active' | 'inactive' | 'archived';
    conversationType: 'campaign_creation' | 'asset_generation' | 'general_chat' | 'mixed';
    messageCount: number;
    startedAt: string;
    lastActiveAt: string;
    createdAt: string;
    updatedAt: string;
    relatedTasks: {
        campaigns: Array<{
            id: string;
            status: string;
            playbookType: string;
        }>;
        assets: Array<{
            id: string;
            status: string;
            assetType: 'video' | 'image';
        }>;
    };
}

export interface ChatDetails extends ChatConversation {
    brandId: number;
    messages: any; // Full conversation history
    relatedTasks: {
        campaigns: any[]; // Full campaign objects
        assets: any[]; // Full asset objects
    };
}

export interface AssetJobDetails {
    id: string;
    brandId: number;
    assetType: 'video' | 'image';
    status: 'draft' | 'generating' | 'completed' | 'failed';
    generationPrompt: string;
    generationParameters: any;
    resultMetadata: any;
    errorMessage: string | null;
    chatId: string | null;
    conversation: {
        id: string;
        conversation_type: string;
        status: string;
    } | null;
    generationStartedAt: string | null;
    generationCompletedAt: string | null;
    createdAt: string;
    updatedAt: string;
    liveStatus: {
        success: boolean;
        assetId: string;
        status: string;
        assetType: string;
        progress: number;
        resultMetadata: any;
        errorMessage: string | null;
        message: string;
    };
}

export interface CampaignExecutorDetails {
    id: string;
    brandId: number;
    strategyId: string | null;
    status: string;
    playbookType: string;
    goalMetadata: any;
    platformReferences: any;
    initialAgentRequest: any;
    chatId: string | null;
    conversation: any;
    createdAt: string;
    updatedAt: string;
    executionStatus: any; // Live status from CampaignExecutor
    executionReasoning: any[]; // Reasoning log
    associatedDbCampaign: any;
    readyForSubmission: boolean;
    completionPercentage: number;
    fieldStatus: {
        hasGoalLink: boolean;
        hasPlaybook: boolean;
        hasCampaignName: boolean;
        hasCreatives: boolean;
        hasBudget: boolean;
        hasAudience: boolean;
        hasTargeting: boolean;
        hasPlatform: boolean;
    };
}

export interface MarketingPlanDetails {
    id: string;
    brandId: number;
    name: string;
    status: string;
    plan_type: string;
    objective: string;
    target_outcomes: string[];
    audience: any;
    budget: any;
    timeline: any;
    actions?: any[]; // Marketing plan actions
    createdAt: string;
    updatedAt: string;
}


const TAGS_INIT = [
    'How can I grow my followers?',
    'How can I get more likes?',
    'How can I get more comments?',
    'How can I get more views?',
    'How many new fans do i have?'
]


/** Client events enum for better type safety and maintainability */
export enum MaestroClientEvents {
    ASSET_GENERATION_COMPLETE = 'ASSET_GENERATION_COMPLETE',     // ✅ Unified event for both videos and images
    VIDEO_GENERATION_COMPLETE = 'VIDEO_GENERATION_COMPLETE',     // Legacy support
    IMAGE_GENERATION_COMPLETE = 'IMAGE_GENERATION_COMPLETE',     // Legacy support
    WEBSITE_BUILD_COMPLETE = 'WEBSITE_BUILD_COMPLETE',
    CAMPAIGN_LAUNCH_COMPLETE = 'CAMPAIGN_LAUNCH_COMPLETE',
    CAMPAIGN_APPROVED_BY_USER = 'CAMPAIGN_APPROVED_BY_USER'      // New event for campaign approval
}

export enum MessageType {
    TEXT = 'text',
    IMAGE = 'image',
    VIDEO = 'video',
    CAMPAIGN_BUILDER_COMPONENT = 'campaignBuilderComponent',
    SUMMARY_CARD = 'summaryCard'
}

/**
 * Interface for storing display-relevant information about an uploaded asset.
 */
export interface UploadedAssetDisplayInfo {
    id: string; // The UploadedAsset ID from the backend
    assetType: 'image' | 'video' | 'audio' | 'document' | 'unknown'; // Broad category
    fileName: string;
    thumbnailUrl?: string; // URL for a preview thumbnail
    originalUrl: string;   // URL for the full asset
    mimeType?: string; // e.g. "image/jpeg", "application/pdf"
}

export interface MessageItem {
    id: number | string
    text: string
    createdAt: string
    isStreaming?: boolean
    isUser: boolean
    isLoadingMessage?: boolean
    parts?: MessagePart[]
    /** Optional media details for images and videos */
    timestamp?: number;
    type?: MessageType
    media?: {
        /** URL of the media file */
        url: string
        /** Type of media (image or video) */
        type: 'image' | 'video'
        /** Thumbnail URL */
        thumbnailUrl: string
        /** Optional video generation context for agent operations */
        videoContext?: {
            publicId: string
            videoId: string
            prompt: string
            generationTime?: number
            metadata?: any
        }
    }
    /** Optional payload for a summary card message */
    summaryCard?: SummaryCardPayload
    /** Optional video generation status for inline progress (legacy - kept for backward compatibility with existing messages) */
    videoGeneration?: {
        videoId: string
        status: any // Legacy status object
        message: string
        estimatedTimeMinutes: number
        startTime: number
    }
    /** Optional asset generation status for inline progress (unified) */
    assetGeneration?: {
        assetId: string
        assetType: 'video' | 'image'
        status: AssetGenerationStatus
        message: string
        estimatedTimeMinutes: number
        startTime: number
    }
    /** Optional list of system messages associated with this assistant message */
    systemMessages?: SystemMessage[]
    /** Optional client event for async workflow notifications */
    clientEvent?: MaestroClientEvents
    /** Optional array of uploaded assets to be displayed with the message */
    uploadedAssets?: UploadedAssetDisplayInfo[];
}

// Message part types
export type MessagePartType = 'text' | 'tool_call' | 'tool_result';

interface TextPart {
    type: 'text';
    content: string;
    id: string;
}

interface ToolCallPart {
    type: 'tool_call';
    toolName: string;
    message: string;
    status: 'running' | 'success' | 'error';
    startTime: number;
    endTime?: number;
    id: string;
    groupId?: string; // For grouping parallel tool calls
}

export type MessagePart = TextPart | ToolCallPart;



// Streaming state to add to your store
interface StreamingState {
    currentParts: MessagePart[];
    currentTextPartId: string | null;
    activeToolCalls: Record<string, ToolCallPart>;
}


// New streaming event types
export interface StreamingEvent {
    type: 'ROUTER_START' | 'SPECIALIST_ACTIVATED' | 'SYSTEM_MESSAGE' | 'TEXT_DELTA' |
    'WORKSPACE_OPEN' | 'WORKSPACE_CLOSE' | 'WORKSPACE_UPDATE' |
    'TOOL_CALL_START' | 'TOOL_CALL_END' | 'STREAM_END' | 'SHOW_SUMMARY_CARD' | 'QUICK_ACTIONS_AVAILABLE' | 'GENERATING_VIDEO' | 'GENERATING_IMAGE' | 'THINKING'
    payload: any
}

// System message interface
export interface SystemMessage {
    id: string
    message: string
    type: 'router_start' | 'specialist_activated' | 'system_message' | 'tool_call_start' | 'tool_call_end'
    timestamp: number
    isVisible: boolean
}

// Tool execution state interface
export interface ToolExecution {
    toolName: string
    toolArgs?: any
    status: 'running' | 'completed' | 'failed'
    startTime: number
    endTime?: number
    message?: string
}

// Available workspace interface for reopening
export interface AvailableWorkspace {
    id: string
    type: string
    data: any
    title?: string
    timestamp: number
}

/** Payload for the SHOW_SUMMARY_CARD event */
export interface SummaryCardPayload {
    imageUrl: string
    title: string
    subtitle: string
    linkUrl?: string
}

/** Client tool function parameters for website builder operations */
export interface CreateWebsiteParams {
    /** Type of website to create */
    websiteType: WebsiteType
    /** Optional website name */
    name?: string
}

export interface UpdateWebsiteColorParams {
    /** Website ID to update */
    websiteId: number
    /** Background color in hex format (#RRGGBB) */
    backgroundColor: string
    /** Secondary color in hex format (#RRGGBB) */
    secondaryColor: string
}

export interface UpdateWebsiteFontParams {
    /** Website ID to update */
    websiteId: number
    /** Font family name */
    fontFamily: string
}

export interface PublishWebsiteParams {
    /** Website ID to publish */
    websiteId: number
}

// Interface for website builder style
interface WebsiteBuilderStyle {
    backgroundColor: string;
    secondaryColor: string;
    textColor: string;
    fontFamily: string;
    textAlign: WebsiteTextAlign;
    isSymphonyLogoEnabled: boolean;
    symphonyLogoStyle: string; // Using string for SymphonyLogoStyleKey
}

/** Quick action types and interfaces */
export type ActionType =
    | 'SEND_MESSAGE'              // Send message as if user typed it
    | 'OPEN_WORKSPACE'            // Open specific workspace
    | 'OPEN_UPLOAD_ASSETS'        // Open asset uploader
    | 'PROMPT'                    // Prompt user for input
    | 'SHOW_MUSIC_SELECTOR'       // Show music selector modal
    | 'OPEN_TASK_PACK_MODAL'      // Show task pack purchase modal
    | 'OPEN_CONDITIONAL_UPGRADE_MODAL'  // Show appropriate upgrade modal based on user status

export interface ActionPayload {
    message?: string              // For SEND_MESSAGE: text to send
    workspaceType?: string        // For OPEN_WORKSPACE: workspace to open
    promptText?: string           // For PROMPT: prompt text to show user
    data?: any           // For OPEN_WORKSPACE: optional workspace data
}

export interface QuickAction {
    id: string                    // Unique identifier
    label: string                 // Button text
    actionType: ActionType        // Action type enum
    // isPrimary: boolean           // Primary button styling
    payload: ActionPayload       // Action-specific data
}



/** Unified payload for asset generation events */
export interface GeneratingAssetPayload {
    assetId: string
    assetType: 'video' | 'image'
    estimatedTimeMinutes: number
    message: string
    videoId?: string  // For backward compatibility with video events
}



/** Thinking state interface for o4-mini reasoning summaries */
export interface ThinkingState {
    isThinking: boolean
    currentThinking: string
    isComplete: boolean
}

/** Unified asset generation status interface */
export interface AssetGenerationStatus {
    success: boolean
    assetId: string
    assetType: 'video' | 'image'
    status: 'draft' | 'generating' | 'completed' | 'failed'
    progress: number
    resultMetadata?: {
        // Video result metadata
        videoUrl?: string
        thumbnailUrl?: string
        publicId?: string
        prompt?: string
        generationTime?: number
        metadata?: any

        // Image result metadata
        images?: Array<{
            cloudinaryUrl: string
            format: string
        }>
        model?: string
        requestType?: string
    }
    errorMessage?: string
    generationPrompt?: string
    createdAt: string
    completedAt?: string
}

/** Active asset generation state */
export interface ActiveAssetGeneration {
    assetId: string
    assetType: 'video' | 'image'
    message: string
    estimatedTimeMinutes: number
    startTime: number
    currentStatus?: AssetGenerationStatus
    pollInterval?: NodeJS.Timeout
}

/** Demo scenario interface */
export interface DemoScenario {
    id: string
    name: string
    description: string
    demoType: 'music' | 'ecommerce' | 'content_creator'
    messages: MessageItem[]
    activeWorkspace?: {
        type: string
        data: any
    }
    systemMessages?: SystemMessage[]
    quickActions?: QuickAction[]
    activeAssetGeneration?: ActiveAssetGeneration
    pendingAssetGeneration?: {
        assetId: string
        assetType: 'video' | 'image'
        status: AssetGenerationStatus
        message: string
        estimatedTimeMinutes: number
        startTime: number
    }
    streamingState?: {
        isStreaming: boolean
        currentStreamedText: string
    }
}



interface MaestroChatState {
    // State
    isChatOpen: boolean
    isLoadingAIResponse: boolean
    messages: MessageItem[]
    userMessageText: string
    maestroTags: string[]
    isStreaming: boolean
    currentStreamedText: string
    isUploading: boolean
    inChatMode: boolean
    chatId: string | null

    // Dev Mode State
    devMode: boolean

    // Demo Mode State
    demoMode: boolean
    demoType: 'music' | 'ecommerce' | 'content_creator' | null
    activeDemoScenario: string | null

    // Request cancellation
    abortController: AbortController | null

    // Voice AI State
    isVoiceEnabled: boolean
    isVoiceListening: boolean
    isVoiceSpeaking: boolean
    voiceVolume: number
    conversationId: string | null
    isVoiceMode: boolean

    // Website Builder Client Tool State
    isWebsiteBuilderAction: boolean
    lastWebsiteBuilderAction: string | null
    lastWebsiteBuilderResult: any | null

    // Template and Building State
    isBuilding: boolean
    buildingMessage: string | null
    pendingTemplateData: any | null

    // New streaming event state
    systemMessages: SystemMessage[]
    currentToolExecution: ToolExecution | null
    activeWorkspace: {
        type: string
        data: any
    } | null
    availableWorkspaces: AvailableWorkspace[]
    pendingSummaryCard: SummaryCardPayload | null

    // Thinking state for o4-mini reasoning summaries
    thinkingState: ThinkingState

    // Quick Actions State
    availableQuickActions: QuickAction[]
    pendingQuickActions: QuickAction[]

    // Unified Asset Generation State
    activeAssetGeneration: ActiveAssetGeneration | null
    pendingAssetGeneration: {
        assetId: string
        assetType: 'video' | 'image'
        status: AssetGenerationStatus
        message: string
        estimatedTimeMinutes: number
        startTime: number
    } | null
    activeAssetPolls: { [key: string]: NodeJS.Timeout },
    streamingState: StreamingState,

    // Tasks & Chats State
    agentJobs: AgentJob[]
    chats: ChatConversation[]
    isLoadingJobs: boolean
    isLoadingChats: boolean
    selectedJobDetails: AssetJobDetails | CampaignExecutorDetails | MarketingPlanDetails | null
    showJobDetailsModal: boolean

    // Actions
    setIsChatOpen: (open: boolean) => void
    setIsLoadingAIResponse: (loading: boolean) => void
    setMessages: (messages: MessageItem[]) => void
    setUserMessageText: (text: string) => void
    setMaestroTags: (tags: string[]) => void
    setIsStreaming: (streaming: boolean) => void
    setCurrentStreamedText: (text: string) => void
    appendToStreamedText: (text: string, type?: 'text' | 'tool_call') => void
    setIsUploading: (uploading: boolean) => void
    setInChatMode: (inChatMode: boolean) => void
    setChatId: (chatId: string | null) => void

    // Dev Mode Actions
    setDevMode: (devMode: boolean) => void
    deleteMessage: (messageId: number | string) => void
    regenerateMessage: (messageId: number | string) => void
    updateMessage: (messageId: number | string, newText: string) => void
    addCustomMessage: (text: string, isUser: boolean) => void

    // Demo Mode Actions
    setDemoMode: (demoMode: boolean) => void
    setDemoType: (demoType: 'music' | 'ecommerce' | 'content_creator' | null) => void
    setActiveDemoScenario: (scenarioId: string | null) => void
    activateDemoScenario: (scenarioId: string, demoType?: 'music' | 'ecommerce' | 'content_creator') => void
    getDemoScenarios: (demoType?: 'music' | 'ecommerce' | 'content_creator') => DemoScenario[]
    generateDemoData: (scenarioId: string, demoType: 'music' | 'ecommerce' | 'content_creator') => DemoScenario
    exitDemoMode: () => void
    clearDemoTimers: () => void
    startDemoAssetProgressSimulation: () => void

    // Voice AI Actions
    setIsVoiceEnabled: (enabled: boolean) => void
    setIsVoiceListening: (listening: boolean) => void
    setIsVoiceSpeaking: (speaking: boolean) => void
    setVoiceVolume: (volume: number) => void
    setConversationId: (id: string | null) => void
    setIsVoiceMode: (isVoiceMode: boolean) => void

    // Website Builder Client Tool Actions
    setIsWebsiteBuilderAction: (isAction: boolean) => void
    setLastWebsiteBuilderAction: (action: string | null) => void
    setLastWebsiteBuilderResult: (result: any | null) => void

    // Template and Building Actions
    setIsBuilding: (building: boolean) => void
    setBuildingMessage: (message: string | null) => void
    setPendingTemplateData: (data: any | null) => void

    // New streaming event actions
    addSystemMessage: (message: SystemMessage) => void
    removeSystemMessage: (id: string) => void
    clearSystemMessages: () => void
    setCurrentToolExecution: (tool: ToolExecution | null) => void
    setActiveWorkspace: (workspace: { type: string; data: any } | null) => void
    addAvailableWorkspace: (workspace: AvailableWorkspace) => void
    setPendingSummaryCard: (payload: SummaryCardPayload | null) => void

    // Thinking actions for o4-mini reasoning summaries
    setThinkingState: (state: ThinkingState) => void
    appendToThinking: (content: string) => void
    completeThinking: (content: string) => void
    clearThinking: () => void

    // Quick Actions
    setAvailableQuickActions: (actions: QuickAction[]) => void
    setPendingQuickActions: (actions: QuickAction[]) => void
    clearQuickActions: () => void

    // Unified Asset Generation Actions
    setActiveAssetGeneration: (generation: ActiveAssetGeneration | null) => void
    startAssetGeneration: (payload: GeneratingAssetPayload) => void
    updateAssetGenerationStatus: (status: AssetGenerationStatus) => void
    completeAssetGeneration: (status: AssetGenerationStatus) => void
    cancelAssetGeneration: () => void

    // Pending Asset Generation Actions
    setPendingAssetGeneration: (generation: {
        assetId: string
        assetType: 'video' | 'image'
        status: AssetGenerationStatus
        message: string
        estimatedTimeMinutes: number
        startTime: number
    } | null) => void
    updatePendingAssetGenerationStatus: (status: AssetGenerationStatus) => void

    // Tasks & Chats Actions
    setAgentJobs: (jobs: AgentJob[]) => void
    setChats: (chats: ChatConversation[]) => void
    setIsLoadingJobs: (loading: boolean) => void
    setIsLoadingChats: (loading: boolean) => void
    setSelectedJobDetails: (details: AssetJobDetails | CampaignExecutorDetails | MarketingPlanDetails | null) => void
    setShowJobDetailsModal: (show: boolean) => void
    fetchJobs: () => Promise<void>
    fetchChats: () => Promise<void>
    fetchJobDetails: (jobId: string, jobType: 'campaign' | 'asset' | 'content_analysis' | 'marketing_plan') => Promise<void>
    resumeChat: (chatId: string) => Promise<void>

    // Event handlers
    handleStreamingEvent: (event: StreamingEvent) => void

    // Complete the streaming and add the message to the chat history
    completeStream: () => void

    // Methods
    sendMessage: (text: string, hiddenData?: { templateData?: any, isClientEvent?: boolean }) => void
    handleFileUpload: (file: CloudinaryUploadedFileType) => void
    clearChatHistory: () => void
    cancelOngoingRequest: () => void

    // Voice AI Methods
    startVoiceSession: () => Promise<void>
    endVoiceSession: () => Promise<void>
    toggleVoice: () => Promise<void>
    adjustVoiceVolume: (volume: number) => Promise<void>

    // Website Builder Client Tool Methods
    createWebsite: (params: CreateWebsiteParams) => Promise<any>
    updateWebsiteColor: (params: UpdateWebsiteColorParams) => Promise<any>
    updateWebsiteFont: (params: UpdateWebsiteFontParams) => Promise<any>
    publishWebsite: (params: PublishWebsiteParams) => Promise<any>

    // Unified asset status polling method
    startAssetStatusPolling: (assetId: string, assetType: 'video' | 'image') => void

    // New method to notify the agent that asset generation is complete and continue the conversation
    notifyAgentAssetComplete: (status: AssetGenerationStatus) => Promise<void>
    // Chat ID generation method
    generateChatId: () => string

    // NEW: Handle multiple file uploads from UniversalUploadWidget
    handleUniversalFileUpload: (uploadedFiles: import('components/shareable/UniversalUploadWidget/UniversalUploadWidget').UploadedFile[]) => Promise<void>
}

const useMaestroChat = create(immer<MaestroChatState>((set, get) => ({
    // State
    isChatOpen: false,
    isLoadingAIResponse: false,
    messages: [],
    userMessageText: '',
    maestroTags: TAGS_INIT,
    isStreaming: false,
    currentStreamedText: '',
    isUploading: false,
    inChatMode: false,
    chatId: null,

    // Dev Mode State
    devMode: false,

    // Demo Mode State
    demoMode: false,
    demoType: null,
    activeDemoScenario: null,

    // Request cancellation
    abortController: null,

    // Voice AI State
    isVoiceEnabled: false,
    isVoiceListening: false,
    isVoiceSpeaking: false,
    voiceVolume: 0.8,
    conversationId: null,
    isVoiceMode: false,

    // Website Builder Client Tool State
    isWebsiteBuilderAction: false,
    lastWebsiteBuilderAction: null,
    lastWebsiteBuilderResult: null,

    // Template and Building State
    isBuilding: false,
    buildingMessage: null,
    pendingTemplateData: null,

    // New streaming event state
    systemMessages: [],
    currentToolExecution: null,
    activeWorkspace: null,
    availableWorkspaces: [],
    pendingSummaryCard: null,

    // Thinking state for o4-mini reasoning summaries
    thinkingState: {
        isThinking: false,
        currentThinking: '',
        isComplete: true
    },
    streamingState: {
        currentParts: [],
        currentTextPartId: null,
        activeToolCalls: {}
    },

    // Quick Actions State
    availableQuickActions: [],
    pendingQuickActions: [],

    // Unified Asset Generation State
    activeAssetGeneration: null,
    pendingAssetGeneration: null,
    activeAssetPolls: {},

    // Tasks & Chats State
    agentJobs: [],
    chats: [],
    isLoadingJobs: false,
    isLoadingChats: false,
    selectedJobDetails: null,
    showJobDetailsModal: false,

    // Actions
    setIsChatOpen: (open) => { set((state) => { state.isChatOpen = open }) },
    setIsLoadingAIResponse: (loading) => { set((state) => { state.isLoadingAIResponse = loading }) },
    setMessages: (messages) => { set((state) => { state.messages = messages }) },
    setUserMessageText: (text) => { set((state) => { state.userMessageText = text }) },
    setMaestroTags: (tags) => { set((state) => { state.maestroTags = tags }) },
    setIsStreaming: (streaming) => { set((state) => { state.isStreaming = streaming }) },
    setCurrentStreamedText: (text) => { set((state) => { state.currentStreamedText = text }) },
    appendToStreamedText: (text, type = 'text') => {
        set((state) => {
            if (type === 'tool_call') {
                // Add special markers that the markdown parser can recognize
                state.currentStreamedText += `__TOOL_CALL__${text}__/TOOL_CALL__`
            } else {
                state.currentStreamedText += text
            }
        })
    },
    setIsUploading: (uploading) => { set((state) => { state.isUploading = uploading }) },
    setInChatMode: (inChatMode) => { set((state) => { state.inChatMode = inChatMode }) },
    setChatId: (chatId) => { set((state) => { state.chatId = chatId }) },

    // Dev Mode Actions
    setDevMode: (devMode) => { set((state) => { state.devMode = devMode }) },
    deleteMessage: (messageId) => {
        set((state) => {
            state.messages = state.messages.filter(msg => msg.id !== messageId)
        })
    },
    regenerateMessage: async (messageId) => {
        const { messages, sendMessage } = get()
        const messageIndex = messages.findIndex(msg => msg.id === messageId)

        if (messageIndex === -1) return

        const targetMessage = messages[messageIndex]

        if (targetMessage.isUser) {
            // If clicking resend on a user message, remove everything from this message onwards and resend it
            set((state) => {
                state.messages = state.messages.slice(0, messageIndex)
            })

            // Resend this user message
            await sendMessage(targetMessage.text)
        } else {
            // If clicking resend on an assistant message, find the user message that triggered it
            let userMessageIndex = messageIndex - 1
            while (userMessageIndex >= 0 && !messages[userMessageIndex].isUser) {
                userMessageIndex--
            }

            if (userMessageIndex >= 0) {
                const userMessage = messages[userMessageIndex]

                // Remove all messages from the assistant message onwards
                set((state) => {
                    state.messages = state.messages.slice(0, messageIndex)
                })

                // Regenerate by sending the user message again
                await sendMessage(userMessage.text)
            }
        }
    },
    updateMessage: (messageId, newText) => {
        set((state) => {
            const messageIndex = state.messages.findIndex(msg => msg.id === messageId)
            if (messageIndex !== -1) {
                state.messages[messageIndex].text = newText
            }
        })
    },
    addCustomMessage: (text, isUser) => {
        set((state) => {
            state.messages.push({
                id: Date.now(),
                text,
                createdAt: new Date().toISOString(),
                isUser,
                type: MessageType.TEXT
            })
        })
    },

    // Demo Mode Actions
    setDemoMode: (demoMode) => {
        set((state) => { state.demoMode = demoMode })
        // Clear demo timers when disabling demo mode
        if (!demoMode) {
            get().clearDemoTimers()
        }
    },
    setDemoType: (demoType) => { set((state) => { state.demoType = demoType }) },
    setActiveDemoScenario: (scenarioId) => { set((state) => { state.activeDemoScenario = scenarioId }) },

    activateDemoScenario: (scenarioId, demoType = 'music') => {
        const scenario = get().generateDemoData(scenarioId, demoType)

        set((state) => {
            // Set demo mode state
            state.demoMode = true
            state.demoType = demoType
            state.activeDemoScenario = scenarioId

            // Apply scenario data
            state.messages = scenario.messages
            state.inChatMode = true
            state.chatId = `demo-${scenarioId}-${Date.now()}`

            // Apply workspace if provided
            if (scenario.activeWorkspace) {
                state.activeWorkspace = scenario.activeWorkspace
            }

            // Apply system messages if provided
            if (scenario.systemMessages) {
                state.systemMessages = scenario.systemMessages
            }

            // Apply quick actions if provided
            if (scenario.quickActions) {
                state.availableQuickActions = scenario.quickActions
            }

            // Apply asset generation if provided
            if (scenario.activeAssetGeneration) {
                state.activeAssetGeneration = scenario.activeAssetGeneration
            }

            if (scenario.pendingAssetGeneration) {
                state.pendingAssetGeneration = scenario.pendingAssetGeneration
            }

            // Apply streaming state if provided
            if (scenario.streamingState) {
                state.isStreaming = scenario.streamingState.isStreaming
                state.currentStreamedText = scenario.streamingState.currentStreamedText
            }
        })

        // Start demo progress simulation for asset generation scenarios
        if (scenarioId === 'video_generation') {
            get().startDemoAssetProgressSimulation()
        }
    },

    getDemoScenarios: (demoType) => {
        const scenarios = [
            'video_generation',
            'marketing_plan_workspace',
            'ig_selector_workspace',
            'campaign_creation',
            'asset_library',
            'website_builder',
            'post_selection_demo',
            'template_selection_demo'
        ]

        return scenarios.map(scenarioId =>
            get().generateDemoData(scenarioId, demoType || 'music')
        )
    },

    generateDemoData: (scenarioId, demoType) => {
        const baseTime = Date.now()

        // Common demo data generators
        const generateMessages = (conversation: Array<{ text: string, isUser: boolean, delay?: number }>) => {
            return conversation.map((msg, index) => ({
                id: baseTime + index,
                text: msg.text,
                createdAt: new Date(baseTime + (msg.delay || index * 30000)).toISOString(),
                isUser: msg.isUser
            }))
        }

        // Demo content based on type
        const demoContent = {
            music: {
                brand: 'Luna Rose',
                product: 'upcoming single "Midnight Dreams"',
                audience: 'indie pop fans aged 18-34',
                platforms: 'Spotify, Apple Music, Instagram',
                budget: '$2,500',
                goals: 'increase streams and build fanbase'
            },
            ecommerce: {
                brand: 'EcoStyle Boutique',
                product: 'sustainable fashion collection',
                audience: 'eco-conscious shoppers aged 25-45',
                platforms: 'Instagram, Facebook, TikTok',
                budget: '$5,000',
                goals: 'drive sales and brand awareness'
            },
            content_creator: {
                brand: 'FitLife Coach',
                product: 'online fitness program',
                audience: 'fitness enthusiasts aged 20-40',
                platforms: 'YouTube, Instagram, TikTok',
                budget: '$3,000',
                goals: 'increase course signups and followers'
            }
        }

        const content = demoContent[demoType] || demoContent['music']; // Fallback to music content for ui_test

        // Handle UI Test Scenarios
        if (demoType === 'music' || demoType === 'ecommerce' || demoType === 'content_creator') {
            let workspaceType = '';
            let workspaceData: any = { isDemo: true, scenarioId }; // Common demo data

            switch (scenarioId) {
                case 'ui_test_campaign_approval_workspace':
                    workspaceType = 'campaignApproval';
                    workspaceData.campaignId = `demo-campaign-${baseTime}`;
                    workspaceData.demoCampaign = { // Mock Campaign data
                        id: `demo-campaign-${baseTime}`,
                        name: `UI Test Campaign - ${content.brand}`,
                        playbook_type: 'INSTAGRAM_MUSIC_PROMOTION',
                        status: 'READY_FOR_SUBMISSION',
                        goal_link: 'https://symphony.is/test-link',
                        agent_notes: { strategy_summary: 'Demo strategy summary.' },
                        campaign_input: {
                            platform: 'meta',
                            budget: 100,
                            budgetType: 'lifetime',
                            length: { startDate: new Date().toISOString(), endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() },
                            creatives: [{
                                source: { type: 'uploaded_asset', mediaUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', cloudinaryId: 'test', assetType: 'image' },
                                caption: 'Test caption',
                                cta: { label: 'Learn More', value: 'LEARN_MORE' }
                            }],
                            goalLink: 'https://symphony.is/test-link',
                            campaignName: `UI Test Campaign - ${content.brand}`,
                            playbookType: 'INSTAGRAM_MUSIC_PROMOTION',
                            totalAdsBudget: 100,
                        },
                        readyForSubmission: true,
                        checkoutDetails: { // Mock checkout details for payment path
                            totalCost: 120,
                            adsBudget: 100,
                            takeRate: 20,
                            lineItems: [{ type: 'ads', name: 'Ad Spend', description: 'Meta Ads', amount: 100 }, { type: 'fee', name: 'Symphony Fee', description: 'Platform Fee', amount: 20 }],
                            currency: 'USD',
                            stripeSession: { paid: false, payment_intent_id: 'pi_demotest', client_secret: 'pi_demotest_secret_demotest' }
                        }
                    };
                    break;
                case 'ui_test_campaign_approval_signup_modal':
                    workspaceType = 'campaignApproval';
                    workspaceData.campaignId = `demo-campaign-signup-${baseTime}`;
                    workspaceData.demoCampaign = { /* ... similar to above ... */
                        id: `demo-campaign-signup-${baseTime}`,
                        name: `UI Test Signup - ${content.brand}`,
                        playbook_type: 'INSTAGRAM_MUSIC_PROMOTION',
                        status: 'READY_FOR_SUBMISSION',
                        goal_link: 'https://symphony.is/test-signup',
                        agent_notes: { strategy_summary: 'Demo strategy for signup modal.' },
                        campaign_input: {
                            platform: 'meta',
                            budget: 50,
                            budgetType: 'daily',
                            length: { startDate: new Date().toISOString(), endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString() },
                            creatives: [{
                                source: { type: 'uploaded_asset', mediaUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', cloudinaryId: 'test_signup', assetType: 'image' },
                                caption: 'Test caption for signup',
                                cta: { label: 'Sign Up', value: 'SIGN_UP' }
                            }],
                            goalLink: 'https://symphony.is/test-signup',
                            campaignName: `UI Test Signup - ${content.brand}`,
                            playbookType: 'INSTAGRAM_MUSIC_PROMOTION',
                            totalAdsBudget: 250,
                        },
                        readyForSubmission: true,
                        checkoutDetails: { /* ... mock checkout ... */ }
                    };
                    workspaceData.showDemoModal = 'signup'; // Specific flag for the modal
                    break;
                case 'ui_test_campaign_approval_subscription_modal':
                    workspaceType = 'campaignApproval';
                    workspaceData.campaignId = `demo-campaign-sub-${baseTime}`;
                    workspaceData.demoCampaign = { /* ... similar to above ... */
                        id: `demo-campaign-sub-${baseTime}`,
                        name: `UI Test Subscription - ${content.brand}`,
                        playbook_type: 'INSTAGRAM_CONVERSIONS',
                        status: 'READY_FOR_SUBMISSION',
                        goal_link: 'https://symphony.is/test-subscription',
                        agent_notes: { strategy_summary: 'Demo strategy for subscription modal.' },
                        campaign_input: {
                             platform: 'tiktok', // Example of different platform
                             budget: 200,
                             budgetType: 'lifetime',
                             length: { startDate: new Date().toISOString(), endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString() },
                             creatives: [{
                                source: { type: 'spark_code', sparkCode: 'DEMO_SPARK_CODE', user_info: {identity_type: 'creator', identity_id: '123', identity_display_name: 'DemoCreator'}, video_info:{file_name:'test.mp4', poster_url:'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', bit_rate:1,size:1,width:1,height:1,signature:'sig',duration:1,preview_url:'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png',video_id:'vid123'}, item_info:{text:'Demo item',item_id:'item123',item_type:'VIDEO', auth_code:'auth', stitch_original_item_id:'orig',duet_original_item_id:'duet',is_multi_duet_stitch:false, anchor_list:null, mentioned_item_ids:null}, mediaUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png'},
                                caption: 'Test caption for subscription',
                                cta: { label: 'Subscribe', value: 'SUBSCRIBE_NOW' }
                             }],
                            goalLink: 'https://symphony.is/test-subscription',
                            campaignName: `UI Test Subscription - ${content.brand}`,
                            playbookType: 'INSTAGRAM_CONVERSIONS',
                            totalAdsBudget: 200,
                        },
                        readyForSubmission: true,
                        checkoutDetails: { /* ... mock checkout ... */ }
                    };
                    workspaceData.showDemoModal = 'subscription'; // Specific flag
                    break;
                case 'ui_test_content_analysis_workspace':
                    workspaceType = 'contentAnalysis';
                    workspaceData.analysisId = `demo-analysis-${baseTime}`;
                    workspaceData.demoAnalysisResult = { // Mock analysis data
                        success: true,
                        analysis: {
                            username: 'testuser',
                            profileData: { followers: 1000, postsCount: 50 },
                            createdAt: new Date().toISOString(),
                            recommendations: {
                                contentThemes: ['Behind the Scenes', 'Tutorials', 'Q&A'],
                                visualElements: ['Bright Colors', 'Clean Backgrounds'],
                                contentPatterns: ['Short Form Video', 'Carousel Posts'],
                                recommendations: ['Post more consistently', 'Engage with comments'],
                                contentIdeas: ['Day in the life video', 'Tutorial on X']
                            },
                            analyzedPosts: [
                                { postMetadata: { caption: 'Demo post 1', username: 'testuser', timestamp: new Date().toISOString(), permalink: '#', engagement: 100, thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png' }, analysis: { detailed_summary: 'This is a great post because...', content_breakdown: { themes_topics: ['Test Theme'], video_narrative: '', visual_elements:[] }, target_audience_analysis: {demographics:[], psychographics:[],public_figures_brands:[], topics:[]} } },
                                { postMetadata: { caption: 'Demo post 2', username: 'testuser', timestamp: new Date().toISOString(), permalink: '#', engagement: 150, thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png' }, analysis: { detailed_summary: 'This post performed well due to...', content_breakdown: { themes_topics: ['Another Theme'], video_narrative: '', visual_elements:[] }, target_audience_analysis: {demographics:[], psychographics:[],public_figures_brands:[], topics:[]} } },
                            ]
                        }
                    };
                    break;
                case 'ui_test_facebook_connect_workspace':
                    workspaceType = 'facebookConnect';
                    workspaceData.reason = 'Connect Facebook for UI testing.';
                    workspaceData.skipOption = true;
                    break;
                case 'ui_test_instagram_post_selection_workspace':
                    workspaceType = 'instagramPostSelection';
                    workspaceData.campaignId = `demo-ig-post-select-${baseTime}`;
                    // Mock data for Instagram posts can be added here if needed by the component directly
                    // For now, it fetches its own posts, but we mark it as demo.
                    break;
                case 'ui_test_instagram_post_selection_cloudinary_modal':
                    workspaceType = 'instagramPostSelection';
                    workspaceData.campaignId = `demo-ig-post-select-cloudinary-${baseTime}`;
                    workspaceData.showDemoModal = 'cloudinary'; // Flag to trigger Cloudinary
                    break;
                case 'ui_test_marketing_plan_workspace':
                    workspaceType = 'marketingPlan';
                    workspaceData.planId = `demo-mktg-plan-${baseTime}`;
                    workspaceData.demoMarketingPlan = { // Mock Marketing Plan
                        id: `demo-mktg-plan-${baseTime}`,
                        brandId: 123,
                        name: 'UI Test Marketing Plan',
                        type: 'launch',
                        campaign: { objective: 'Test objective', targetOutcomes: ['Outcome 1', 'Outcome 2'] },
                        audience: { primaryPersona: { label: 'Test Persona', age: '25-35', demographics: ['Urban'], geographies: ['USA'], interests: ['Test Interest'] } },
                        budget: { total: 5000, tier: 'medium', allocation: { ads: 3000, content: 1000, tools: 1000 } },
                        timeline: { phases: [{ name: 'Phase 1', startDate: new Date().toISOString(), endDate: new Date().toISOString(), focus: 'Initial Setup', actions: [{ id: 'action1', type: 'symphony_tool', title: 'Setup Campaign', description: 'Initial campaign setup', priority: 'high', status: 'pending' }] }] },
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                        status: 'draft',
                    };
                    break;
                case 'ui_test_signup_flow_workspace': // This will likely be rendered inside a modal by another workspace
                    workspaceType = 'signupFlow';       // Or could be a direct view if app supports it
                    workspaceData.config = {
                        triggerType: 'campaign_approval',
                        title: 'UI Test: Signup Flow',
                        description: 'Test the signup flow component.',
                        signupCallToAction: 'Test Sign Up',
                        recommendedPlan: 'STARTER',
                        source: 'DemoControls_UITest_SignupFlow'
                    };
                    break;
                case 'ui_test_subscription_flow_workspace':
                    workspaceType = 'subscriptionFlow';
                    workspaceData.config = {
                        flowType: 'campaign_approval',
                        title: 'UI Test: Subscription Flow',
                        description: 'Test the subscription flow.',
                        upgradeCallToAction: 'Test Subscribe',
                        highlightedPlan: 'GROWTH',
                        source: 'DemoControls_UITest_SubscriptionFlow',
                        campaignPricing: {
                            totalCost: 120, adsBudget: 100, takeRate: 20,
                            lineItems: [{ type: 'ads', name: 'Ad Spend', amount: 100, description:'' }, { type: 'fee', name: 'Symphony Fee', amount: 20, description:'' }],
                            currency: 'USD'
                        }
                    };
                    break;
                case 'ui_test_template_selection_workspace':
                    workspaceType = 'templateSelection';
                    workspaceData.templateCategories = ['music', 'popular']; // Example: only allow music and popular
                    break;
                case 'ui_test_website_preview_workspace':
                    workspaceType = 'websitePreview';
                    workspaceData.websiteId = `demo-website-${baseTime}`;
                    workspaceData.slug = `demo-website-${baseTime}`;
                    workspaceData.status = 'DRAFT';
                    workspaceData.websiteContent = { // Mock website content
                        content: [
                            { id: '1', type: 'TEXT', contentIndex: 0, textContent: { title: 'Demo Website Title', subtitle: 'This is a subtitle for the demo.' } },
                            { id: '2', type: 'IMAGE', contentIndex: 1, imageContent: { src: 'https://res.cloudinary.com/symphony-images/image/upload/v1700083091/symphony/brand_content/r3xifp0hnk3cnlmsiisz.png', alt: 'Demo Image' } }
                        ]
                    };
                    workspaceData.websiteStyle = { // Mock website style
                        backgroundColor: '#FFFFFF',
                        secondaryColor: '#000000',
                        textColor: '#333333',
                        fontFamily: 'Arial, sans-serif',
                        textAlign: 'left',
                        isSymphonyLogoEnabled: true,
                        symphonyLogoStyle: 'dark',
                    };
                    break;
                default:
                    // Fallback for unknown UI test scenario
                    return {
                        id: scenarioId,
                        name: 'Unknown UI Test',
                        description: `Unknown UI test scenario: ${scenarioId}`,
                        demoType,
                        messages: generateMessages([{ text: `Attempting to load UI test: ${scenarioId}`, isUser: false }]),
                        activeWorkspace: { type: 'unknown', data: { scenarioId } }
                    } as DemoScenario;
            }

            return {
                id: scenarioId,
                name: `UI Test: ${workspaceType}`,
                description: `Testing ${workspaceType} component.`,
                demoType,
                messages: [], // No messages needed for direct UI component testing
                activeWorkspace: {
                    type: workspaceType,
                    data: workspaceData
                }
            } as DemoScenario;
        }

        // Original Demo Scenarios
        switch (scenarioId) {
            case 'video_generation':
                // Create a message with assetGeneration attached to the last AI message
                const videoMessages = generateMessages([
                    { text: `Hi! I need to create a promotional video for ${content.product}. Can you help?`, isUser: true },
                    { text: `Absolutely! I'd love to help you create a promotional video for ${content.product}. Let me ask you a few questions to understand your vision better.`, isUser: false },
                    { text: `What's the main message you want to convey in this video?`, isUser: false, delay: 2000 },
                    { text: `I want to showcase the emotional connection and energy of the song. Something that captures the dreamy, midnight vibe with urban aesthetics.`, isUser: true, delay: 30000 },
                    { text: `Perfect! That gives me a great direction. I'll create a video that captures that dreamy midnight aesthetic with urban elements. Let me generate this for you...`, isUser: false, delay: 32000 }
                ])

                // Add assetGeneration to the last AI message
                const lastMessage = videoMessages[videoMessages.length - 1] as MessageItem
                if (!lastMessage.isUser) {
                    lastMessage.assetGeneration = {
                        assetId: `demo-video-${baseTime}`,
                        assetType: 'video' as const,
                        message: 'Generating your promotional video with dreamy midnight urban aesthetics...',
                        estimatedTimeMinutes: 3,
                        startTime: baseTime,
                        status: {
                            success: false,
                            assetId: `demo-video-${baseTime}`,
                            assetType: 'video' as const,
                            status: 'generating' as const,
                            progress: 45,
                            generationPrompt: 'Dreamy midnight urban aesthetic promotional video',
                            createdAt: new Date(baseTime).toISOString()
                        }
                    }
                }

                return {
                    id: scenarioId,
                    name: 'Video Generation Demo',
                    description: `${content.brand} creates promotional video`,
                    demoType,
                    messages: videoMessages
                } as DemoScenario

            case 'marketing_plan_workspace':
                return {
                    id: scenarioId,
                    name: 'Marketing Plan Workspace Demo',
                    description: `${content.brand} marketing strategy session`,
                    demoType,
                    messages: generateMessages([
                        { text: `I need help creating a comprehensive marketing strategy for ${content.product}. My goal is to ${content.goals}.`, isUser: true },
                        { text: `Excellent! I'll help you create a strategic marketing plan. Let me open up our marketing planning workspace where we can map out your strategy, budget allocation, and campaign timeline.`, isUser: false },
                        { text: `I've analyzed your brand and created a comprehensive marketing plan. Take a look at the strategy I've outlined for you.`, isUser: false, delay: 5000 }
                    ]),
                    activeWorkspace: {
                        type: 'marketingPlan',
                        data: {
                            planId: `demo-plan-${demoType}-${baseTime}`,
                            name: content.brand + ' Marketing Strategy',
                            status: 'active',
                            type: 'launch',
                            isDemo: true,
                            // Complete marketing plan object for demo
                            demoMarketingPlan: {
                                id: `demo-plan-${demoType}-${baseTime}`,
                                brandId: 12345,
                                name: content.brand + ' Marketing Strategy',
                                type: 'launch' as const,
                                campaign: {
                                    objective: `Launch and promote ${content.product} to ${content.goals}`,
                                    targetOutcomes: [
                                        'Increase brand awareness by 50%',
                                        'Generate 10K new followers',
                                        'Achieve 5% engagement rate',
                                        'Drive 2.5K website visits'
                                    ],
                                    launchDate: new Date(baseTime + 7 * 24 * 60 * 60 * 1000).toISOString(),
                                    endDate: new Date(baseTime + 37 * 24 * 60 * 60 * 1000).toISOString()
                                },
                                audience: {
                                    primaryPersona: {
                                        label: demoType === 'music' ? 'Indie Music Lovers' :
                                            demoType === 'ecommerce' ? 'Conscious Shoppers' : 'Fitness Enthusiasts',
                                        age: content.audience.split(' aged ')[1] || '18-34',
                                        demographics: content.audience.split(' aged ')[0]?.split(', ') || ['Urban professionals'],
                                        geographies: ['United States', 'Canada', 'United Kingdom'],
                                        interests: demoType === 'music' ? ['Indie Music', 'Alternative Rock', 'Live Music', 'Music Discovery'] :
                                            demoType === 'ecommerce' ? ['Sustainable Fashion', 'Eco-Friendly Products', 'Conscious Shopping', 'Minimalist Lifestyle'] :
                                                ['Fitness', 'Health & Wellness', 'Personal Training', 'Workout Routines']
                                    }
                                },
                                budget: {
                                    total: parseInt(content.budget.replace(/\D/g, '')),
                                    tier: parseInt(content.budget.replace(/\D/g, '')) > 5000 ? 'large' :
                                        parseInt(content.budget.replace(/\D/g, '')) > 2000 ? 'medium' : 'small',
                                    allocation: {
                                        ads: Math.floor(parseInt(content.budget.replace(/\D/g, '')) * 0.6),
                                        content: Math.floor(parseInt(content.budget.replace(/\D/g, '')) * 0.25),
                                        tools: Math.floor(parseInt(content.budget.replace(/\D/g, '')) * 0.15)
                                    }
                                },
                                timeline: {
                                    phases: [
                                        {
                                            name: 'Setup & Creative Development',
                                            startDate: new Date(baseTime).toISOString(),
                                            endDate: new Date(baseTime + 7 * 24 * 60 * 60 * 1000).toISOString(),
                                            focus: 'Brand setup and content creation',
                                            actions: [
                                                {
                                                    id: 'action-1',
                                                    type: 'symphony_tool' as const,
                                                    title: 'Create Social Media Assets',
                                                    description: 'Generate branded visuals and promotional content',
                                                    platform: 'Instagram',
                                                    symphonyTool: 'Creative Generator',
                                                    estimatedTime: '2 hours',
                                                    priority: 'high' as const,
                                                    status: 'completed' as const
                                                },
                                                {
                                                    id: 'action-2',
                                                    type: 'organic_content' as const,
                                                    title: 'Write Campaign Copy',
                                                    description: 'Develop compelling captions and ad copy',
                                                    estimatedTime: '3 hours',
                                                    priority: 'high' as const,
                                                    status: 'completed' as const
                                                }
                                            ]
                                        },
                                        {
                                            name: 'Launch Phase',
                                            startDate: new Date(baseTime + 7 * 24 * 60 * 60 * 1000).toISOString(),
                                            endDate: new Date(baseTime + 21 * 24 * 60 * 60 * 1000).toISOString(),
                                            focus: 'Campaign activation and monitoring',
                                            actions: [
                                                {
                                                    id: 'action-3',
                                                    type: 'symphony_tool' as const,
                                                    title: 'Launch Instagram Campaign',
                                                    description: 'Activate paid advertising on Instagram and Facebook',
                                                    platform: 'Instagram',
                                                    symphonyTool: 'Campaign Manager',
                                                    budget: Math.floor(parseInt(content.budget.replace(/\D/g, '')) * 0.4),
                                                    estimatedTime: '1 hour',
                                                    priority: 'high' as const,
                                                    status: 'in_progress' as const
                                                },
                                                {
                                                    id: 'action-4',
                                                    type: 'organic_content' as const,
                                                    title: 'Daily Social Media Posts',
                                                    description: 'Post engaging content to build organic reach',
                                                    platform: content.platforms,
                                                    estimatedTime: '30 min/day',
                                                    priority: 'medium' as const,
                                                    status: 'in_progress' as const
                                                }
                                            ]
                                        },
                                        {
                                            name: 'Optimization & Scale',
                                            startDate: new Date(baseTime + 21 * 24 * 60 * 60 * 1000).toISOString(),
                                            endDate: new Date(baseTime + 37 * 24 * 60 * 60 * 1000).toISOString(),
                                            focus: 'Performance optimization and scaling',
                                            actions: [
                                                {
                                                    id: 'action-5',
                                                    type: 'symphony_tool' as const,
                                                    title: 'A/B Test Ad Creatives',
                                                    description: 'Test different creative variations for optimal performance',
                                                    platform: 'Instagram',
                                                    symphonyTool: 'Performance Optimizer',
                                                    estimatedTime: '2 hours',
                                                    priority: 'medium' as const,
                                                    status: 'pending' as const
                                                },
                                                {
                                                    id: 'action-6',
                                                    type: 'external_strategy' as const,
                                                    title: 'Influencer Outreach',
                                                    description: 'Partner with micro-influencers in target niche',
                                                    estimatedTime: '4 hours',
                                                    priority: 'low' as const,
                                                    status: 'pending' as const
                                                }
                                            ]
                                        }
                                    ]
                                },
                                createdAt: new Date(baseTime).toISOString(),
                                updatedAt: new Date(baseTime).toISOString(),
                                status: 'active' as const
                            }
                        }
                    },
                    systemMessages: [
                        {
                            id: `system-${baseTime}`,
                            message: 'Analyzing brand data and market trends...',
                            type: 'system_message',
                            timestamp: baseTime,
                            isVisible: true
                        }
                    ]
                } as DemoScenario

            case 'ig_selector_workspace':
                return {
                    id: scenarioId,
                    name: 'Instagram Campaign Setup Demo',
                    description: `${content.brand} Instagram campaign configuration`,
                    demoType,
                    messages: generateMessages([
                        { text: `I want to run Instagram ads for ${content.product}. Can you help me set up the campaign?`, isUser: true },
                        { text: `Absolutely! Instagram is perfect for ${demoType === 'music' ? 'music promotion' : demoType === 'ecommerce' ? 'showcasing products' : 'fitness content'}. Let me set up your campaign targeting and creative options.`, isUser: false },
                        { text: `I've prepared your Instagram campaign setup. Review the targeting options and let me know if you'd like to adjust anything.`, isUser: false, delay: 3000 }
                    ]),
                    activeWorkspace: {
                        type: 'instagramPostSelection',
                        data: {
                            campaignId: `demo-ig-campaign-${baseTime}`,
                            isDemo: true
                        }
                    },
                    quickActions: [
                        {
                            id: 'launch-campaign',
                            label: 'Launch Campaign',
                            actionType: 'SEND_MESSAGE' as ActionType,
                            payload: {
                                message: 'Yes, launch this Instagram campaign with these settings!'
                            }
                        },
                        {
                            id: 'adjust-budget',
                            label: 'Adjust Budget',
                            actionType: 'PROMPT' as ActionType,
                            payload: {
                                promptText: 'What budget would you like to set for this campaign?'
                            }
                        },
                        {
                            id: 'modify-targeting',
                            label: 'Modify Targeting',
                            actionType: 'OPEN_WORKSPACE' as ActionType,
                            payload: {
                                workspaceType: 'advanced_targeting'
                            }
                        },
                        {
                            id: 'upload-assets',
                            label: 'Upload Creative Assets',
                            actionType: 'OPEN_UPLOAD_ASSETS' as ActionType,
                            payload: {}
                        }
                    ]
                } as DemoScenario

            case 'campaign_creation':
                return {
                    id: scenarioId,
                    name: 'Campaign Creation Demo',
                    description: `${content.brand} comprehensive campaign setup`,
                    demoType,
                    messages: generateMessages([
                        { text: `I need to create a complete marketing campaign for ${content.product}. Where do I start?`, isUser: true },
                        { text: `Great! Let's create a comprehensive campaign for ${content.product}. I'll guide you through the entire process from strategy to launch.`, isUser: false },
                        { text: `First, let me understand your goals and target audience better. What's your primary objective for this campaign?`, isUser: false, delay: 2000 },
                        { text: `My main goal is to ${content.goals} with a budget of ${content.budget}.`, isUser: true, delay: 30000 },
                        { text: `Perfect! Based on your goals and budget, I'm setting up a multi-platform campaign strategy. Let me create the campaign framework for you.`, isUser: false, delay: 32000 }
                    ]),
                    activeWorkspace: {
                        type: 'campaignApproval',
                        data: {
                            campaignId: `demo-campaign-builder-${baseTime}`,
                            isDemo: true,
                            // Complete demo campaign object for campaign builder
                            demoCampaign: {
                                id: `demo-campaign-builder-${baseTime}`,
                                name: `${content.brand} - ${new Date().getFullYear()} Campaign`,
                                playbook_type: demoType === 'music' ? 'INSTAGRAM_MUSIC_PROMOTION' :
                                    demoType === 'ecommerce' ? 'INSTAGRAM_CONVERSIONS' : 'INSTAGRAM_LEAD_GENERATION',
                                status: 'READY_FOR_SUBMISSION',
                                goal_link: demoType === 'music' ? 'https://open.spotify.com/track/demo-midnight-dreams' :
                                    demoType === 'ecommerce' ? 'https://shop.ecostyle.com/collection' :
                                        'https://fitlife.com/program',
                                agent_notes: {
                                    strategy_summary: `Comprehensive ${content.platforms} campaign to ${content.goals} with ${content.budget} budget`,
                                    one_liner: demoType === 'music' ? 'Dreamy indie vibes for late-night music discovery' :
                                        demoType === 'ecommerce' ? 'Sustainable fashion for conscious consumers' :
                                            'Transform your fitness journey with expert guidance',
                                    targeted_audiences: demoType === 'music' ? ['Indie Music Fans', 'Alternative Rock Listeners', 'Music Discovery', 'Concert Goers'] :
                                        demoType === 'ecommerce' ? ['Sustainable Fashion', 'Eco-Conscious Shoppers', 'Minimalist Lifestyle'] :
                                            ['Fitness Enthusiasts', 'Health Conscious', 'Personal Training', 'Workout Routines'],
                                    targeted_locations: ['United States', 'Canada', 'United Kingdom', 'Australia'],
                                    estimation_expectations: `Expecting strong performance in target demographics with optimized ad spend`,
                                    budget_guidance: `${content.budget} budget optimally allocated across platforms and creative testing`
                                },
                                campaign_input: {
                                    platform: 'meta',
                                    budget: parseInt(content.budget.replace(/\D/g, '')),
                                    budgetType: 'lifetime',
                                    length: {
                                        startDate: new Date().toISOString(),
                                        endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                                    },
                                    creatives: [
                                        {
                                            id: 'creative-1',
                                            type: 'video',
                                            url: 'https://demo-video-url.com',
                                            caption: `Discover ${content.product} - ${demoType === 'music' ? 'Stream now on all platforms' : demoType === 'ecommerce' ? 'Shop sustainable fashion' : 'Start your fitness transformation'}`,
                                            cta: {
                                                label: demoType === 'music' ? 'Listen Now' : demoType === 'ecommerce' ? 'Shop Now' : 'Learn More',
                                                value: 'LEARN_MORE'
                                            }
                                        }
                                    ]
                                },
                                readyForSubmission: true
                            }
                        }
                    },
                    systemMessages: [
                        {
                            id: `system-${baseTime}`,
                            message: 'Setting up campaign structure...',
                            type: 'system_message',
                            timestamp: baseTime,
                            isVisible: true
                        }
                    ]
                } as DemoScenario

            case 'post_selection_demo':
                return {
                    id: scenarioId,
                    name: 'Instagram Post Selection Demo',
                    description: `${content.brand} selects Instagram content for campaigns`,
                    demoType,
                    messages: generateMessages([
                        { text: `I want to use my existing Instagram posts for my ${content.product} campaign. Can you help me choose the best ones?`, isUser: true },
                        { text: `Absolutely! Using your existing Instagram content is a great strategy. I'll help you select the most engaging posts that align with your campaign goals.`, isUser: false },
                        { text: `Let me open your Instagram post selection workspace where you can browse and choose from your recent posts.`, isUser: false, delay: 2000 }
                    ]),
                    activeWorkspace: {
                        type: 'instagramPostSelection',
                        data: {
                            campaignId: `demo-post-selection-${baseTime}`,
                            isDemo: true
                        }
                    },
                    systemMessages: [
                        {
                            id: `system-${baseTime}`,
                            message: 'Loading Instagram posts and analyzing engagement metrics...',
                            type: 'system_message',
                            timestamp: baseTime,
                            isVisible: true
                        }
                    ]
                } as DemoScenario

            case 'template_selection_demo':
                return {
                    id: scenarioId,
                    name: 'Website Template Selection Demo',
                    description: `${content.brand} chooses website template`,
                    demoType,
                    messages: generateMessages([
                        { text: `I need to build a website for ${content.product}. What templates do you recommend?`, isUser: true },
                        { text: `Great choice! A professional website will really help ${content.goals}. Let me show you some templates that would work perfectly for ${demoType === 'music' ? 'musicians and artists' : demoType === 'ecommerce' ? 'e-commerce businesses' : 'fitness professionals'}.`, isUser: false },
                        { text: `I've filtered the templates to show the best options for your industry. Take a look and let me know which style appeals to you!`, isUser: false, delay: 3000 }
                    ]),
                    activeWorkspace: {
                        type: 'templateSelection',
                        data: {
                            templateCategories: demoType === 'music' ? ['music'] :
                                demoType === 'ecommerce' ? ['popular'] :
                                    ['popular'],
                            isDemo: true
                        }
                    },
                    systemMessages: [
                        {
                            id: `system-${baseTime}`,
                            message: 'Analyzing brand style and filtering template recommendations...',
                            type: 'system_message',
                            timestamp: baseTime,
                            isVisible: true
                        }
                    ]
                } as DemoScenario

            default:
                return {
                    id: scenarioId,
                    name: 'Default Demo',
                    description: 'Basic demo scenario',
                    demoType,
                    messages: generateMessages([
                        { text: `Hi! I'm ${content.brand} and I need help with ${content.product}.`, isUser: true },
                        { text: `Hello! I'd be happy to help you with ${content.product}. What specific assistance do you need?`, isUser: false }
                    ])
                } as DemoScenario
        }
    },

    exitDemoMode: () => {
        set((state) => {
            // Reset demo mode state
            state.demoMode = false
            state.demoType = null
            state.activeDemoScenario = null

            // Clear all chat data
            state.messages = []
            state.inChatMode = false
            state.chatId = null

            // Clear workspaces and interactions
            state.activeWorkspace = null
            state.availableWorkspaces = []
            state.systemMessages = []
            state.availableQuickActions = []
            state.pendingQuickActions = []

            // Clear streaming state
            state.isStreaming = false
            state.currentStreamedText = ''
            state.streamingState = {
                currentParts: [],
                currentTextPartId: null,
                activeToolCalls: {}
            }

            // Clear asset generation
            state.activeAssetGeneration = null
            state.pendingAssetGeneration = null

            // Clear thinking state
            state.thinkingState = {
                isThinking: false,
                currentThinking: '',
                isComplete: true
            }

            // Clear loading states
            state.isLoadingAIResponse = false
            state.isUploading = false
            state.isBuilding = false
            state.buildingMessage = null
            state.pendingTemplateData = null
        })

        // Clear any demo timers
        get().clearDemoTimers()
    },

    clearDemoTimers: () => {
        const { activeAssetPolls } = get()
        Object.values(activeAssetPolls).forEach(clearInterval)
        set((state) => {
            state.activeAssetPolls = {}
        })
    },

    startDemoAssetProgressSimulation: () => {
        const { messages } = get()

        // Find the message with asset generation
        const messageWithAsset = messages.find(msg =>
            !msg.isUser && msg.assetGeneration && msg.assetGeneration.assetType === 'video'
        )

        if (!messageWithAsset?.assetGeneration) return

        const assetId = messageWithAsset.assetGeneration.assetId
        let progress = 45 // Start at 45% as set in demo data

        // Update progress every 2 seconds until completion (3 minutes = 180 seconds)
        const progressInterval = setInterval(() => {
            progress += Math.random() * 8 + 2 // Increase by 2-10% each tick

            if (progress >= 100) {
                progress = 100
                clearInterval(progressInterval)

                // Mark as completed with demo result
                set((state) => {
                    const msgIndex = state.messages.findIndex(msg =>
                        msg.assetGeneration?.assetId === assetId
                    )
                    if (msgIndex !== -1 && state.messages[msgIndex].assetGeneration) {
                        state.messages[msgIndex].assetGeneration!.status = {
                            success: true,
                            assetId,
                            assetType: 'video',
                            status: 'completed',
                            progress: 100,
                            generationPrompt: 'Dreamy midnight urban aesthetic promotional video',
                            createdAt: new Date().toISOString(),
                            completedAt: new Date().toISOString(),
                            resultMetadata: {
                                videoUrl: 'https://res.cloudinary.com/symphony-images/video/upload/v1699901234/demo_video_placeholder.mp4',
                                thumbnailUrl: 'https://res.cloudinary.com/symphony-images/image/upload/v1699901234/demo_video_thumbnail.jpg',
                                publicId: 'demo_video_placeholder',
                                prompt: 'Dreamy midnight urban aesthetic promotional video',
                                generationTime: 180000, // 3 minutes
                                metadata: { demoVideo: true }
                            }
                        }
                    }

                    // Remove from active polls
                    delete state.activeAssetPolls[assetId]
                })
            } else {
                // Update progress
                set((state) => {
                    const msgIndex = state.messages.findIndex(msg =>
                        msg.assetGeneration?.assetId === assetId
                    )
                    if (msgIndex !== -1 && state.messages[msgIndex].assetGeneration) {
                        state.messages[msgIndex].assetGeneration!.status.progress = Math.floor(progress)
                    }
                })
            }
        }, 2000) // Update every 2 seconds

        // Store the interval so it can be cleared
        set((state) => {
            state.activeAssetPolls[assetId] = progressInterval
        })
    },

    // Voice AI Actions
    setIsVoiceEnabled: (enabled) => { set((state) => { state.isVoiceEnabled = enabled }) },
    setIsVoiceListening: (listening) => { set((state) => { state.isVoiceListening = listening }) },
    setIsVoiceSpeaking: (speaking) => { set((state) => { state.isVoiceSpeaking = speaking }) },
    setVoiceVolume: (volume) => { set((state) => { state.voiceVolume = volume }) },
    setConversationId: (id) => { set((state) => { state.conversationId = id }) },
    setIsVoiceMode: (isVoiceMode) => { set((state) => { state.isVoiceMode = isVoiceMode }) },

    // Website Builder Client Tool Actions
    setIsWebsiteBuilderAction: (isAction) => { set((state) => { state.isWebsiteBuilderAction = isAction }) },
    setLastWebsiteBuilderAction: (action) => { set((state) => { state.lastWebsiteBuilderAction = action }) },
    setLastWebsiteBuilderResult: (result) => { set((state) => { state.lastWebsiteBuilderResult = result }) },

    // Template and Building Actions
    setIsBuilding: (building) => { set((state) => { state.isBuilding = building }) },
    setBuildingMessage: (message) => { set((state) => { state.buildingMessage = message }) },
    setPendingTemplateData: (data) => { set((state) => { state.pendingTemplateData = data }) },

    // New streaming event actions
    addSystemMessage: (message) => { set((state) => { state.systemMessages.push(message) }) },
    removeSystemMessage: (id) => { set((state) => { state.systemMessages = state.systemMessages.filter(m => m.id !== id) }) },
    clearSystemMessages: () => { set((state) => { state.systemMessages = [] }) },
    setCurrentToolExecution: (tool) => { set((state) => { state.currentToolExecution = tool }) },
    setActiveWorkspace: (workspace) => { set((state) => { state.activeWorkspace = workspace }) },
    addAvailableWorkspace: (workspace) => {
        set((state) => {
            // Remove any existing workspace of the same type to avoid duplicates
            state.availableWorkspaces = state.availableWorkspaces.filter(w => w.type !== workspace.type)
            // Add the new workspace
            state.availableWorkspaces.push(workspace)
        })
    },
    setPendingSummaryCard: (payload) => {
        set((state) => {
            state.pendingSummaryCard = payload
        })
    },

    // Thinking actions for o4-mini reasoning summaries
    setThinkingState: (thinkingState) => { set((state) => { state.thinkingState = thinkingState }) },
    appendToThinking: (content) => {
        set((state) => {
            state.thinkingState.currentThinking += content
            state.thinkingState.isThinking = true
            state.thinkingState.isComplete = false
        })
    },
    completeThinking: (content) => {
        set((state) => {
            state.thinkingState.currentThinking = content
            state.thinkingState.isThinking = false
            state.thinkingState.isComplete = true
        })
    },
    clearThinking: () => {
        set((state) => {
            state.thinkingState.isThinking = false
            state.thinkingState.currentThinking = ''
            state.thinkingState.isComplete = true
        })
    },

    // Quick Actions
    setAvailableQuickActions: (actions) => { set((state) => { state.availableQuickActions = actions }) },
    setPendingQuickActions: (actions) => { set((state) => { state.pendingQuickActions = actions }) },
    clearQuickActions: () => {
        set((state) => {
            state.availableQuickActions = []
            state.pendingQuickActions = []
        })
    },

    // Unified Asset Generation Actions
    setActiveAssetGeneration: (generation) => { set((state) => { state.activeAssetGeneration = generation }) },

    startAssetGeneration: (payload) => {
        const { assetId, assetType, estimatedTimeMinutes, message } = payload

        set((state) => {
            state.activeAssetGeneration = {
                assetId,
                assetType,
                message,
                estimatedTimeMinutes,
                startTime: Date.now()
            }
        })

        // Start polling for asset status
        get().startAssetStatusPolling(assetId, assetType)
    },

    updateAssetGenerationStatus: (status) => {
        set((state) => {
            if (state.activeAssetGeneration && state.activeAssetGeneration.assetId === status.assetId) {
                state.activeAssetGeneration.currentStatus = status
            }
        })
    },

    completeAssetGeneration: (status) => {
        const { activeAssetGeneration } = get()

        if (activeAssetGeneration) {
            // Clear the polling interval
            if (activeAssetGeneration.pollInterval) {
                clearInterval(activeAssetGeneration.pollInterval)
            }

            // If successful, add the asset to the chat
            if (status.status === 'completed' && status.resultMetadata) {
                set((state) => {
                    if (status.assetType === 'video' && status.resultMetadata?.videoUrl) {
                        // Add video to chat
                        state.messages.push({
                            id: state.messages.length + 1,
                            text: '✅ Your video is ready!',
                            createdAt: new Date().toISOString(),
                            isUser: false,
                            media: {
                                url: status.resultMetadata.videoUrl,
                                type: 'video',
                                thumbnailUrl: status.resultMetadata.thumbnailUrl || status.resultMetadata.videoUrl,
                                videoContext: {
                                    publicId: status.resultMetadata.publicId || '',
                                    videoId: status.assetId,
                                    prompt: status.resultMetadata.prompt || '',
                                    generationTime: status.resultMetadata.generationTime,
                                    metadata: status.resultMetadata.metadata
                                }
                            }
                        })
                    } else if (status.assetType === 'image' && status.resultMetadata?.images) {
                        // Add images to chat
                        state.messages.push({
                            id: state.messages.length + 1,
                            text: '✅ Your images are ready!',
                            createdAt: new Date().toISOString(),
                            isUser: false,
                            media: {
                                url: status.resultMetadata.images[0]?.cloudinaryUrl || '',
                                type: 'image',
                                thumbnailUrl: status.resultMetadata.images[0]?.cloudinaryUrl || ''
                            }
                        })
                    }
                })
            } else if (status.status === 'failed') {
                // Add error message to chat
                set((state) => {
                    state.messages.push({
                        id: state.messages.length + 1,
                        text: `❌ ${status.assetType === 'video' ? 'Video' : 'Image'} generation failed: ${status.errorMessage}`,
                        createdAt: new Date().toISOString(),
                        isUser: false
                    })
                })
            }

            // Clear the active asset generation
            set((state) => {
                state.activeAssetGeneration = null
            })
        }
    },

    cancelAssetGeneration: () => {
        const { activeAssetGeneration } = get()

        if (activeAssetGeneration?.pollInterval) {
            clearInterval(activeAssetGeneration.pollInterval)
        }

        set((state) => {
            state.activeAssetGeneration = null
        })
    },

    // Pending Asset Generation Actions
    setPendingAssetGeneration: (generation) => { set((state) => { state.pendingAssetGeneration = generation }) },

    updatePendingAssetGenerationStatus: (status) => {
        set((state) => {
            if (state.pendingAssetGeneration && state.pendingAssetGeneration.assetId === status.assetId) {
                state.pendingAssetGeneration.status = status
            }
        })
    },

    // Tasks & Chats Actions
    setAgentJobs: (jobs) => { set((state) => { state.agentJobs = jobs }) },
    setChats: (chats) => { set((state) => { state.chats = chats }) },
    setIsLoadingJobs: (loading) => { set((state) => { state.isLoadingJobs = loading }) },
    setIsLoadingChats: (loading) => { set((state) => { state.isLoadingChats = loading }) },
    setSelectedJobDetails: (details) => { set((state) => { state.selectedJobDetails = details }) },
    setShowJobDetailsModal: (show) => { set((state) => { state.showJobDetailsModal = show }) },
    // USES AXIOS
    fetchJobs: async () => {
        set((state) => { state.isLoadingJobs = true })
        try {
            const { currentBrand } = useBrand.getState()
            if (!currentBrand?.id) {
                console.error('No brand selected')
                set((state) => { state.isLoadingJobs = false })
                return
            }

            try {
                const response = await Axios.post('/agents/jobs', {
                    brandId: currentBrand.id
                })

                if (!response.data.isError) {
                    set((state) => { state.agentJobs = response.data.tasks })
                } else {
                    console.error('Failed to fetch tasks:', response.data.message)
                    set((state) => { state.agentJobs = [] })
                }
            } catch (error) {
                console.error('Error fetching tasks from API:', error)
                set((state) => { state.agentJobs = [] })
            }
        } catch (error) {
            console.error('Error getting tasks:', error)
            set((state) => { state.agentJobs = [] })
        } finally {
            set((state) => { state.isLoadingJobs = false })
        }
    },

    // USES AXIOS
    fetchChats: async () => {
        set((state) => { state.isLoadingChats = true })
        try {
            const { currentBrand } = useBrand.getState()
            if (!currentBrand?.id) {
                console.error('No brand selected')
                set((state) => { state.isLoadingChats = false })
                return
            }

            try {
                const response = await Axios.post('/agents/chats', {
                    brandId: currentBrand.id
                })

                if (!response.isError) {
                    set((state) => { state.chats = response.data.chats })
                } else {
                    console.error('Failed to fetch chats:', response.isError)
                    set((state) => { state.chats = [] })
                }
            } catch (error) {
                console.error('Error fetching chats from API:', error)
                set((state) => { state.chats = [] })
            }
        } catch (error) {
            console.error('Error getting chats:', error)
            set((state) => { state.chats = [] })
        } finally {
            set((state) => { state.isLoadingChats = false })
        }
    },
    // USES AXIOS
    fetchJobDetails: async (jobId, jobType) => {
        try {
            const { currentBrand } = useBrand.getState()
            if (!currentBrand?.id) {
                console.error('No brand selected')
                return
            }



            // Standard job details handling for other job types
            let endpoint: string
            let response: any

            if (jobType === 'campaign') {
                endpoint = `/agents/campaign-executor/${jobId}`
                response = await Axios.post(endpoint, {
                    brandId: currentBrand.id
                })
            } else if (jobType === 'asset') {
                endpoint = `/agents/asset-job/${jobId}`
                response = await Axios.post(endpoint, {
                    brandId: currentBrand.id
                })
            } else if (jobType === 'content_analysis') {
                // For content analysis, jobId is actually the analysisId
                // and we need to use GET instead of POST
                endpoint = `/agents/content-analysis/${jobId}`
                response = await Axios.get(endpoint)
            } else if (jobType === 'marketing_plan') {
                // For marketing plans, use the marketing plan API
                const planDetails = await getMarketingPlan_api(currentBrand.id, jobId)
                if (planDetails) {
                    set((state) => {
                        state.selectedJobDetails = planDetails as any // Type assertion to avoid complex transformation
                        state.showJobDetailsModal = true
                    })
                } else {
                    console.error('Failed to fetch marketing plan details')
                    set((state) => { state.selectedJobDetails = null })
                }
                return
            } else {
                console.error('Unknown job type:', jobType)
                return
            }

            if (response.data.success || response.data) {
                set((state) => {
                    state.selectedJobDetails = response.data.data || response.data.analysis || response.data
                    state.showJobDetailsModal = true
                })
            } else {
                console.error('Failed to fetch job details:', response.data.message)
                set((state) => { state.selectedJobDetails = null })
            }
        } catch (error) {
            console.error('Error getting job details:', error)
            set((state) => { state.selectedJobDetails = null })
        }
    },

    // USES AXIOS
    resumeChat: async (chatId) => {
        try {
            const { currentBrand } = useBrand.getState()
            if (!currentBrand?.id) {
                console.error('No brand selected')
                return
            }

            // Fetch chat details
            const response = await Axios.post(`/agents/chat/${chatId}`, {
                brandId: currentBrand.id
            })

            if (!response.isError) {
                const chatDetails: ChatDetails = response.data

                // Transform API messages to MessageItem format
                // Handle both StoredMessage format from DB and legacy MessageItem format
                const transformedMessages: MessageItem[] = (chatDetails.messages || []).map((apiMsg: any, index: number) => {
                    // If this is already in MessageItem format (legacy)
                    if (apiMsg.text !== undefined && apiMsg.isUser !== undefined) {
                        return {
                            id: apiMsg.id || index + 1,
                            text: apiMsg.text || '',
                            createdAt: apiMsg.createdAt || new Date().toISOString(),
                            isUser: apiMsg.isUser,
                            type: apiMsg.type || MessageType.TEXT,
                            ...(apiMsg.metadata && { metadata: apiMsg.metadata }),
                            ...(apiMsg.summaryCard && { summaryCard: apiMsg.summaryCard }),
                            ...(apiMsg.assetGeneration && { assetGeneration: apiMsg.assetGeneration }),
                            ...(apiMsg.systemMessages && { systemMessages: apiMsg.systemMessages }),
                            ...(apiMsg.parts && { parts: apiMsg.parts })
                        };
                    }
                    
                    // Handle StoredMessage format from database
                    return {
                        id: apiMsg.metadata?.original_id || apiMsg.id || index + 1,
                        text: apiMsg.content || '',
                        createdAt: apiMsg.timestamp ? new Date(apiMsg.timestamp).toISOString() : new Date().toISOString(),
                        isUser: apiMsg.type === 'user',
                        type: MessageType.TEXT,
                        ...(apiMsg.metadata && { metadata: apiMsg.metadata })
                    };
                })

                // Set the chat ID and messages to resume the conversation
                set((state) => {
                    state.chatId = chatId
                    state.messages = transformedMessages
                    state.inChatMode = true
                })
                
                console.log(`✅ Successfully resumed chat ${chatId} with ${transformedMessages.length} messages`);
            } else {
                console.error('Failed to fetch chat details:', response.data.message)
            }
        } catch (error) {
            console.error('Error resuming chat:', error)
        }
    },

    // Event handlers
    handleStreamingEvent: (event) => {
        const {
            addSystemMessage,
            setCurrentToolExecution,
            setActiveWorkspace,
            appendToStreamedText,
            completeStream,
            setPendingSummaryCard,
            setAvailableQuickActions
        } = get()

        const streamingState = get().streamingState;
        const { currentParts, activeToolCalls } = streamingState;

        switch (event.type) {
            case 'ROUTER_START':
                addSystemMessage({
                    id: `router-${Date.now()}`,
                    message: event.payload.message || 'Maestro is thinking...',
                    type: 'router_start',
                    timestamp: Date.now(),
                    isVisible: true
                })
                break

            case 'SPECIALIST_ACTIVATED':
                addSystemMessage({
                    id: `specialist-${Date.now()}`,
                    message: event.payload.message || `${event.payload.name} specialist activated`,
                    type: 'specialist_activated',
                    timestamp: Date.now(),
                    isVisible: true
                })
                break

            case 'SYSTEM_MESSAGE':
                addSystemMessage({
                    id: `system-${Date.now()}`,
                    message: event.payload.message,
                    type: 'system_message',
                    timestamp: Date.now(),
                    isVisible: true
                })
                break
            case 'TEXT_DELTA':
                if (!get().isStreaming) {
                    set((state) => {
                        state.isStreaming = true;
                        state.isLoadingAIResponse = false;
                    });
                }


                // FIXED: Use immutable state updates
                set((state) => {
                    const { currentParts, currentTextPartId } = state.streamingState;

                    // Find existing text part
                    const textPartIndex = currentParts.findIndex(
                        p => p.type === 'text' && p.id === currentTextPartId
                    );

                    let newParts;
                    let newTextPartId = currentTextPartId;

                    if (textPartIndex === -1) {
                        // Create new text part
                        const newTextPart: TextPart = {
                            type: 'text' as const,
                            content: event.payload.content,
                            id: `text-${Date.now()}`
                        };
                        newParts = [...currentParts, newTextPart];
                        newTextPartId = newTextPart.id;
                    } else {
                        // Update existing text part immutably
                        newParts = currentParts.map((part, index) => {
                            if (index === textPartIndex && part.type === 'text') {
                                return {
                                    ...part,
                                    content: part.content + event.payload.content
                                };
                            }
                            return part;
                        });
                    }

                    return {
                        streamingState: {
                            ...state.streamingState,
                            currentParts: newParts,
                            currentTextPartId: newTextPartId
                        }
                    };
                });

                // Still update the legacy text for backward compatibility
                appendToStreamedText(event.payload.content);
                break

            case 'SHOW_SUMMARY_CARD':
                setPendingSummaryCard(event.payload)
                break

            case 'QUICK_ACTIONS_AVAILABLE':
                // If streaming is active, store actions as pending to show after text completes
                // Otherwise, show them immediately
                const { isStreaming } = get()
                if (isStreaming) {
                    get().setPendingQuickActions(event.payload.actions || [])
                } else {
                    setAvailableQuickActions(event.payload.actions || [])
                }
                break

            case 'WORKSPACE_OPEN':
                setActiveWorkspace({
                    type: event.payload.workspaceType,
                    data: event.payload.data || {}
                })
                // Also add to available workspaces for reopening
                get().addAvailableWorkspace({
                    id: `workspace-${Date.now()}`,
                    type: event.payload.workspaceType,
                    data: event.payload.data || {},
                    title: event.payload.title,
                    timestamp: Date.now()
                })
                break

            case 'WORKSPACE_CLOSE':
                // Close workspace if it matches the type being closed
                const currentWorkspace = get().activeWorkspace
                if (currentWorkspace && currentWorkspace.type === event.payload.workspaceType) {
                    setActiveWorkspace(null)
                }
                break

            case 'WORKSPACE_UPDATE':
                // Update existing workspace data
                const workspace = get().activeWorkspace
                if (workspace && workspace.type === event.payload.workspaceType) {
                    setActiveWorkspace({
                        type: workspace.type,
                        data: { ...workspace.data, ...event.payload.data }
                    })
                }
                break

            case 'TOOL_CALL_START':
                // Check if this tool should be shown in UI
                const showToolCallInUI = shouldShowToolCall(event.payload.toolName);
                const showInSystemMsgs = shouldShowInSystemMessages(event.payload.toolName);

                if (showToolCallInUI) {
                    // Get custom message for this tool
                    const customMessage = getToolCallStartMessage(event.payload.toolName, event.payload.message);

                    // Close current text part
                    set((state) => {
                        const newTextPartId = null; // Close current text part

                        // Create tool call part
                        const toolCallPart: ToolCallPart = {
                            type: 'tool_call' as const,
                            toolName: event.payload.toolName,
                            message: customMessage,
                            status: 'running' as const,
                            startTime: Date.now(),
                            id: `tool-${Date.now()}-${event.payload.toolName}`
                        };

                        // Check if this is part of a parallel group
                        const isFirstToolInBatch = Object.keys(state.streamingState.activeToolCalls).length === 0;
                        if (!isFirstToolInBatch && state.streamingState.currentParts.length > 0) {
                            const lastPart = state.streamingState.currentParts[state.streamingState.currentParts.length - 1];
                            if (lastPart.type === 'tool_call') {
                                toolCallPart.groupId = lastPart.groupId || lastPart.id;
                            }
                        }

                        // Create new object for activeToolCalls (immutable)
                        const newActiveToolCalls = { ...state.streamingState.activeToolCalls };
                        newActiveToolCalls[event.payload.toolName] = toolCallPart;

                        return {
                            streamingState: {
                                ...state.streamingState,
                                currentParts: [...state.streamingState.currentParts, toolCallPart],
                                currentTextPartId: newTextPartId,
                                activeToolCalls: newActiveToolCalls
                            }
                        };
                    });

                    // Keep existing functionality
                    setCurrentToolExecution({
                        toolName: event.payload.toolName,
                        toolArgs: event.payload.toolArgs,
                        status: 'running',
                        message: customMessage,
                        startTime: Date.now()
                    })
                }

                // Add system message only if configured to show
                if (showInSystemMsgs) {
                    addSystemMessage({
                        id: `tool-start-${Date.now()}`,
                        message: `Executing: ${event.payload.toolName}`,
                        type: 'tool_call_start',
                        timestamp: Date.now(),
                        isVisible: true
                    })
                }
                break

            case 'TOOL_CALL_END':
                // Check if this tool should be shown in UI/system messages
                const showEndInUI = shouldShowToolCall(event.payload.toolName);
                const showEndInSystemMsgs = shouldShowInSystemMessages(event.payload.toolName);

                if (showEndInUI) {
                    set((state) => {
                        const toolName = event.payload.toolName;
                        const activeToolCalls = state.streamingState.activeToolCalls;

                        if (activeToolCalls[toolName]) {
                            // Update the tool call part in currentParts
                            const updatedParts = state.streamingState.currentParts.map(part => {
                                if (part.type === 'tool_call' && part.toolName === toolName) {
                                    return {
                                        ...part,
                                        status: event.payload.status === 'success' ? 'success' : 'error',
                                        endTime: Date.now()
                                    };
                                }
                                return part;
                            });

                            // Create new Map without this tool
                            const newActiveToolCalls = { ...activeToolCalls };
                            delete newActiveToolCalls[toolName];

                            return {
                                streamingState: {
                                    ...state.streamingState,
                                    currentParts: updatedParts,
                                    activeToolCalls: newActiveToolCalls
                                }
                            };
                        }
                        return state;
                    });

                    // Keep existing functionality
                    const currentTool = get().currentToolExecution
                    if (currentTool) {
                        setCurrentToolExecution({
                            ...currentTool,
                            status: event.payload.status || 'completed',
                            endTime: Date.now()
                        })
                    }
                }

                // Add system message only if configured to show
                if (showEndInSystemMsgs) {
                    // Get custom message based on status
                    const customMessage = event.payload.status === 'success'
                        ? getToolCallSuccessMessage(event.payload.toolName, event.payload.message)
                        : getToolCallErrorMessage(event.payload.toolName, event.payload.message);

                    addSystemMessage({
                        id: `tool-end-${Date.now()}`,
                        message: customMessage,
                        type: 'tool_call_end',
                        timestamp: Date.now(),
                        isVisible: true
                    })
                }
                break

            case 'STREAM_END':
                completeStream()
                // Clear tool execution when stream ends
                setCurrentToolExecution(null)

                // Don't auto-hide system messages - let them stay visible
                break

            case 'GENERATING_VIDEO':
                // Unified video generation using asset generation system
                const { videoId, estimatedTimeMinutes, message } = event.payload
                const assetId = event.payload.assetId || videoId // Use assetId if available, fallback to videoId

                const initialAssetStatus: AssetGenerationStatus = {
                    success: false,
                    assetId,
                    assetType: 'video',
                    status: 'draft',
                    progress: 10,
                    generationPrompt: message || 'Starting video generation...',
                    createdAt: new Date().toISOString()
                }

                get().setPendingAssetGeneration({
                    assetId,
                    assetType: 'video',
                    message,
                    estimatedTimeMinutes,
                    startTime: Date.now(),
                    status: initialAssetStatus
                })

                // Start polling for asset status using the unified endpoint
                get().startAssetStatusPolling(assetId, 'video')
                break

            case 'GENERATING_IMAGE':
                // Unified image generation
                const { assetId: imageAssetId, estimatedTimeMinutes: imageEstTime, message: imageMessage } = event.payload

                const initialImageStatus: AssetGenerationStatus = {
                    success: false,
                    assetId: imageAssetId,
                    assetType: 'image',
                    status: 'draft',
                    progress: 10,
                    generationPrompt: imageMessage || 'Starting image generation...',
                    createdAt: new Date().toISOString()
                }

                get().setPendingAssetGeneration({
                    assetId: imageAssetId,
                    assetType: 'image',
                    message: imageMessage,
                    estimatedTimeMinutes: imageEstTime,
                    startTime: Date.now(),
                    status: initialImageStatus
                })

                // Start polling for asset status
                get().startAssetStatusPolling(imageAssetId, 'image')
                break

            case 'THINKING':
                // Handle o4-mini reasoning summaries
                const { content, type: reasoningType, isComplete } = event.payload

                if (reasoningType === 'reasoning_summary') {
                    // Streaming delta - append to current thinking
                    get().appendToThinking(content)
                } else if (reasoningType === 'final_reasoning_summary') {
                    // Final summary - complete the thinking process
                    get().completeThinking(content)
                } else {
                    // Fallback for any other thinking content
                    get().appendToThinking(content)
                }
                break

            default:
                console.warn('Unknown streaming event type:', event.type)
        }
    },

    // Complete the streaming and add the message to the chat history
    completeStream: () => {
        const {
            currentStreamedText,
            pendingSummaryCard,
            pendingAssetGeneration,
            systemMessages,
            thinkingState,
            pendingQuickActions,
            streamingState // NEW: Get streaming state
        } = get();

        // Check if we have content to add (now also checking for parts)
        if (currentStreamedText.trim() || pendingSummaryCard || pendingAssetGeneration || streamingState.currentParts.length > 0) {
            set((state) => {
                // Create the new message with parts
                const newMessage = {
                    id: state.messages.length + 1,
                    text: currentStreamedText,
                    createdAt: new Date().toISOString(),
                    isUser: false,
                    summaryCard: state.pendingSummaryCard || undefined,
                    assetGeneration: state.pendingAssetGeneration || undefined,
                    systemMessages: state.systemMessages,
                    parts: [...state.streamingState.currentParts] // NEW: Include parts
                };

                state.messages.push(newMessage);

                // Clear all streaming-related state
                state.currentStreamedText = '';
                state.pendingSummaryCard = null;
                state.pendingAssetGeneration = null;
                state.systemMessages = [];

                // NEW: Reset streaming state
                state.streamingState = {
                    currentParts: [],
                    currentTextPartId: null,
                    activeToolCalls: {}
                };
            });
        }

        // Mark thinking as complete when stream ends, but keep the content visible
        if (thinkingState.isThinking || thinkingState.currentThinking) {
            set((state) => {
                state.thinkingState.isThinking = false;
                state.thinkingState.isComplete = true;
            });
        }

        // Move pending quick actions to available quick actions now that text is complete
        if (pendingQuickActions.length > 0) {
            set((state) => {
                state.availableQuickActions = state.pendingQuickActions;
                state.pendingQuickActions = [];
            });
        }

        // Always stop streaming
        set((state) => {
            state.isStreaming = false;
        });
    },


    // Methods
    sendMessage: async (text: string, hiddenData?: { templateData?: any, isClientEvent?: boolean }) => {
        const { currentBrand: primaryBrand, ensureBrandAvailable } = useBrand.getState();


        // Skip text validation for client events
        if (!hiddenData?.isClientEvent && !text.trim()) {
            return;
        }

        // Handle slash commands for dev mode and demo mode
        if (!hiddenData?.isClientEvent && text.trim().startsWith('/')) {
            const command = text.trim().toLowerCase();

            if (command === '/devmode') {
                const currentDevMode = get().devMode;
                get().setDevMode(!currentDevMode);

                // Add system message to chat to confirm the action
                set((state) => {
                    state.messages.push({
                        id: state.messages.length + 1,
                        text: `Dev mode ${!currentDevMode ? 'enabled' : 'disabled'} ✨`,
                        createdAt: new Date().toISOString(),
                        isUser: false
                    });
                });
                return;
            }

            if (command === '/demomode') {
                const currentDemoMode = get().demoMode;
                const wasDevModeDisabled = !get().devMode;

                // Enable dev mode first if not already enabled (since demo mode requires dev mode)
                if (wasDevModeDisabled) {
                    get().setDevMode(true);
                }

                get().setDemoMode(!currentDemoMode);

                // Add system message to chat to confirm the action
                set((state) => {
                    state.messages.push({
                        id: state.messages.length + 1,
                        text: `Demo mode ${!currentDemoMode ? 'enabled' : 'disabled'} 🎭${wasDevModeDisabled ? ' (Dev mode also enabled)' : ''}`,
                        createdAt: new Date().toISOString(),
                        isUser: false
                    });
                });
                return;
            }

            // If it's an unrecognized slash command, continue with normal processing
        }

        // Generate chatId if this is a new chat session
        let { chatId } = get()
        if (!chatId) {
            chatId = get().generateChatId()
            get().setChatId(chatId)
        }



        // Handle template data if provided
        if (hiddenData?.templateData) {
            set((state) => {
                state.pendingTemplateData = hiddenData.templateData;
                state.isBuilding = true;
                state.buildingMessage = 'Building your website with the selected template...';
            });
        }

        // Only add user message to chat if it's not a client event
        if (!hiddenData?.isClientEvent) {
            set((state) => {
                state.isLoadingAIResponse = true;
                state.messages.push({
                    id: state.messages.length + 1,
                    text,
                    createdAt: new Date().toISOString(),
                    isUser: true
                });
                // Clear system messages from previous conversation when starting a new message
                state.systemMessages = [];
                // Clear pending summary card from previous message
                state.pendingSummaryCard = null;
                // Clear thinking state from previous message
                state.thinkingState = {
                    isThinking: false,
                    currentThinking: '',
                    isComplete: true
                };
            });
        } else {
            // For client events, just set up the streaming state
            set((state) => {
                state.isLoadingAIResponse = true;
                // Clear system messages from previous conversation when starting a new message
                state.systemMessages = [];
                // Clear pending summary card from previous message
                state.pendingSummaryCard = null;
                // Clear thinking state from previous message
                state.thinkingState = {
                    isThinking: false,
                    currentThinking: '',
                    isComplete: true
                };
            });
        }

        try {
            // Get current message history to send to API (after adding the user message)
            const currentMessages = [...get().messages];

            // Brand should already be available from PublicRoutes initialization
            let brandId = primaryBrand?.id;
            if (!brandId) {
                const newBrand = await ensureBrandAvailable();
                brandId = newBrand?.id;
            }

            // Real API streaming call
            try {
                // Create new AbortController for this request
                const abortController = new AbortController();

                // Set up streaming state with abort controller
                set({
                    isStreaming: false,
                    isLoadingAIResponse: true,
                    currentStreamedText: '',
                    abortController: abortController
                });

                // Get authentication token
                const token = localStorage.getItem('accessToken');

                // Prepare the request payload
                const requestPayload: any = {
                    messages: currentMessages,
                    brandId: brandId,
                    chatId: chatId
                };

                // Add template data if available
                if (hiddenData?.templateData) {
                    requestPayload.templateData = hiddenData.templateData;
                }
                // Track the accumulated response for parsing
                let accumulatedResponse = ''

                // Make streaming API request with proper streaming config
                await axios({
                    method: 'POST',
                    url: `${process.env.REACT_APP_API_URL}/maestro`,
                    data: requestPayload,
                    headers: {
                        'Content-Type': 'application/json',
                        'brand-id': brandId
                    },
                    responseType: 'text',
                    cancelToken: new axios.CancelToken((cancel) => {
                        // Store the cancel function so we can call it if needed
                        set((state) => {
                            state.abortController = { abort: cancel } as any;
                        });
                    }),
                    onDownloadProgress: (progressEvent) => {
                        // Check if request was cancelled
                        if (get().abortController === null) {
                            return;
                        }

                        const fullResponse = progressEvent.currentTarget.response;

                        // Get only the new chunk since last update
                        const newChunk = fullResponse.substring(accumulatedResponse.length);
                        accumulatedResponse = fullResponse;
                        if (newChunk.trim()) {
                            // Parse each line as a potential JSON event
                            const lines = newChunk.split('\n');
                            for (const line of lines) {
                                const trimmedLine = line.trim();
                                if (trimmedLine) {
                                    try {
                                        const event = JSON.parse(trimmedLine);
                                        get().handleStreamingEvent(event);
                                    } catch (error) {
                                        // If it's not valid JSON, treat as legacy text delta
                                        console.error('Failed to parse streaming event:', error);
                                        console.error('Raw line that failed to parse:', trimmedLine);
                                    }
                                }
                            }
                        }
                    }
                });

                // Finish the stream
                // completeStream();
            } catch (error) {
                // Check if the error is due to cancellation
                if (axios.isCancel(error)) {
                    console.log('Request was cancelled');
                    // Don't proceed with fallback if request was cancelled
                    return;
                }

                console.error('Error with streaming API:', error);

            } finally {
                set({
                    isLoadingAIResponse: false,
                    isBuilding: false,
                    isStreaming: false,
                    buildingMessage: null,
                    pendingTemplateData: null,
                    abortController: null
                });
            }
        } catch (error) {
            console.error('Error sending message:', error);
            set((state) => {
                state.isLoadingAIResponse = false;
                state.isStreaming = false;
                state.isBuilding = false;
                state.buildingMessage = null;
                state.pendingTemplateData = null;
                state.abortController = null;
            });
        }
    },

    clearChatHistory: () => {
        // Cancel any ongoing request first
        get().cancelOngoingRequest()

        const { activeAssetPolls } = get()
        Object.values(activeAssetPolls).forEach(clearInterval)

        // Generate new chatId for the new chat session
        const newChatId = get().generateChatId()

        set((state) => {
            state.messages = []
            state.isLoadingAIResponse = false
            state.isStreaming = false
            state.currentStreamedText = ''
            state.isBuilding = false
            state.buildingMessage = null
            state.pendingTemplateData = null
            state.abortController = null
            state.chatId = newChatId
            // Clear new streaming state
            state.systemMessages = []
            state.currentToolExecution = null
            state.activeWorkspace = null
            state.availableWorkspaces = []
            state.pendingSummaryCard = null
            // Clear streaming state
            state.streamingState = {
                currentParts: [],
                currentTextPartId: null,
                activeToolCalls: {}
            }
            // Clear thinking state
            state.thinkingState = {
                isThinking: false,
                currentThinking: '',
                isComplete: true
            }
            // Clear quick actions
            state.availableQuickActions = []
            state.pendingQuickActions = []
            // Clear unified asset generation
            state.activeAssetGeneration = null
            state.pendingAssetGeneration = null
            state.activeAssetPolls = {}
        })
    },

    // Voice AI Methods
    startVoiceSession: async () => {
        try {
            // This function will be implemented in the component using the useConversation hook
            set((state) => {
                state.isVoiceEnabled = true
                state.isVoiceListening = true
                state.isVoiceMode = true
            })
        } catch (error) {
            console.error('Error starting voice session:', error)
            set((state) => {
                state.isVoiceEnabled = false
                state.isVoiceListening = false
                state.isVoiceMode = false
            })
        }
    },

    endVoiceSession: async () => {
        try {
            // This function will be implemented in the component using the useConversation hook
            set((state) => {
                state.isVoiceEnabled = false
                state.isVoiceListening = false
                state.isVoiceSpeaking = false
                state.conversationId = null
                state.isVoiceMode = false
            })
        } catch (error) {
            console.error('Error ending voice session:', error)
        }
    },

    toggleVoice: async () => {
        const isEnabled = get().isVoiceEnabled
        if (isEnabled) {
            await get().endVoiceSession()
        } else {
            await get().startVoiceSession()
        }
    },

    adjustVoiceVolume: async (volume: number) => {
        set((state) => { state.voiceVolume = volume })
        // This function will be implemented in the component using the useConversation hook
    },

    // Website Builder Client Tool Methods
    createWebsite: async (params: CreateWebsiteParams) => {
        set((state) => {
            state.isWebsiteBuilderAction = true
            state.lastWebsiteBuilderAction = 'createWebsite'
        })

        try {
            // Get the current brand
            const { currentBrand } = useBrand.getState()
            const brandId = currentBrand?.id

            if (!brandId) {
                throw new Error('No brand selected')
            }

            // Initialize website builder store with defaults
            const websiteBuilder = useWebsiteBuilder.getState()

            // Generate a slug based on the website name or use default
            const slug = params.name
                ? params.name.toLowerCase().replace(/[^a-z0-9]/g, '-')
                : `new-website-${Date.now()}`

            // Set basic website properties
            websiteBuilder.setWebsiteSlugText(slug)
            websiteBuilder.resetWebsiteBuilder() // Reset to default styles and empty content
            websiteBuilder.setInitialWebsiteType(params.websiteType)

            // Create default content based on website type
            let initialContent: WebsiteBuilderContent[] = []

            if (params.websiteType === WebsiteType.PRESAVE) {
                // Create a presave website with basic content
                initialContent = [
                    {
                        id: `text-${Date.now()}-1`,
                        type: WebsiteBuilderContentType.TEXT,
                        contentIndex: 0,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        textContent: {
                            title: params.name || 'New Presave',
                            subtitle: 'Pre-save my upcoming release',
                            isSubtitle: true
                        }
                    },
                    {
                        id: `presave-${Date.now()}-2`,
                        type: WebsiteBuilderContentType.PRESAVE,
                        contentIndex: 1,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        presaveContent: {
                            title: 'Pre-save',
                            style: PresaveWebsiteBuilderStyle.TRADITIONAL,
                            hideReleaseDate: false,
                            presave: {
                                boosts: {
                                    auto_presaves: true,
                                    boost_follows: false,
                                    collect_emails: true
                                },
                                platforms: [
                                    { name: PresavePlatform.SPOTIFY, isEnabled: true },
                                    { name: PresavePlatform.APPLE_MUSIC, isEnabled: true },
                                    { name: PresavePlatform.AUDIOMACK, isEnabled: false },
                                    { name: PresavePlatform.SOUNDCLOUD, isEnabled: false },
                                    { name: PresavePlatform.DEEZER, isEnabled: false }
                                ],
                                release_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
                            }
                        }
                    }
                ]
            } else if (params.websiteType === WebsiteType.RECORD_STREAMS) {
                // Create a record streams website with basic content
                initialContent = [
                    {
                        id: `text-${Date.now()}-1`,
                        type: WebsiteBuilderContentType.TEXT,
                        contentIndex: 0,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        textContent: {
                            title: params.name || 'My Music',
                            subtitle: 'Listen to my music on your favorite platform',
                            isSubtitle: true
                        }
                    },
                    {
                        id: `streaming-${Date.now()}-2`,
                        type: WebsiteBuilderContentType.STREAMING_PLATFORMS,
                        contentIndex: 1,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        streamingPlatformsContent: {
                            title: 'Listen Now',
                            // Convert to the required format for PlatformItem
                            platforms: STREAMING_PLATFORMS_WEBSITE_CONTENT.map(platform => ({
                                title: platform.name,
                                description: `Listen on ${platform.name}`,
                                name: platform.name,
                                icon: platform.name.toLowerCase(),
                                link: platform.link,
                                isEnabled: ['spotify', 'apple_music'].includes(platform.name),
                                placeholder: platform.placeholder
                            })),
                            style: StreamingPlatformsStyle.BOXES
                        }
                    }
                ]
            } else if (params.websiteType === WebsiteType.DATA_COLLECTOR) {
                // Create a data collector website with basic content
                initialContent = [
                    {
                        id: `text-${Date.now()}-1`,
                        type: WebsiteBuilderContentType.TEXT,
                        contentIndex: 0,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        textContent: {
                            title: params.name || 'Join My List',
                            subtitle: 'Subscribe to get updates about my music',
                            isSubtitle: true
                        }
                    },
                    {
                        id: `collect-data-${Date.now()}-2`,
                        type: WebsiteBuilderContentType.COLLECT_DATA,
                        contentIndex: 1,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        collectDataContent: {
                            title: 'Subscribe',
                            buttonText: 'Subscribe',
                            postSubscribeMessage: 'Thank you for subscribing!',
                            isBothEmailPhone: false,
                            isShowTitles: true,
                            info: [
                                {
                                    title: 'Name',
                                    placeholder: 'Your name',
                                    type: CollectDataInputType.TEXT,
                                    isRequired: false
                                },
                                {
                                    title: 'Email',
                                    placeholder: 'Your email',
                                    type: CollectDataInputType.TEXT,
                                    isRequired: true
                                }
                            ]
                        }
                    }
                ]
            } else if (params.websiteType === WebsiteType.TOUR) {
                // Create a tour website with basic content
                initialContent = [
                    {
                        id: `text-${Date.now()}-1`,
                        type: WebsiteBuilderContentType.TEXT,
                        contentIndex: 0,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        textContent: {
                            title: params.name || 'My Tour Dates',
                            subtitle: 'Come see me live on tour',
                            isSubtitle: true
                        }
                    },
                    {
                        id: `tour-${Date.now()}-2`,
                        type: WebsiteBuilderContentType.TOUR,
                        contentIndex: 1,
                        isContentBackgroundColorEnabled: false,
                        isContentSecondaryColorEnabled: false,
                        tourContent: {
                            title: 'Tour Dates',
                            tourDates: [],
                            hidePastTourDates: true,
                            style: TourStyle.STACKED
                        }
                    }
                ]
            }

            // Set the content in the website builder store
            websiteBuilder.setWebsiteContent(initialContent)

            // Create the website
            const result = await websiteBuilder.createWebsite()

            if (!result) {
                throw new Error('Failed to create website')
            }

            // Prepare response
            const response = {
                success: true,
                websiteId: result.id,
                websiteType: params.websiteType,
                name: params.name || 'New Website',
                status: result.status,
                createdAt: result.createdAt,
                slug: result.slug,
                websiteUrl: `https://${currentBrand?.slug}.symphony.is/${result.slug}`
            }

            set((state) => { state.lastWebsiteBuilderResult = response })
            return response
        } catch (error) {
            console.error('Error creating website:', error)
            const errorResult = { success: false, error: 'Failed to create website' }
            set((state) => { state.lastWebsiteBuilderResult = errorResult })
            return errorResult
        } finally {
            set((state) => { state.isWebsiteBuilderAction = false })
        }
    },

    updateWebsiteColor: async (params: UpdateWebsiteColorParams) => {
        set((state) => {
            state.isWebsiteBuilderAction = true
            state.lastWebsiteBuilderAction = 'updateWebsiteColor'
        })

        try {
            // Get the website builder store
            const websiteBuilder = useWebsiteBuilder.getState()

            // First load the website to update
            await websiteBuilder.getWebsite({ websiteId: params.websiteId })

            // Get the current website style
            const currentStyle = { ...websiteBuilder.websiteStyle }

            // Set the updated style with required properties
            websiteBuilder.setWebsiteStyle({
                ...currentStyle,
                backgroundColor: params.backgroundColor,
                secondaryColor: params.secondaryColor
            })

            // Update the website
            const result = await websiteBuilder.updateWebsite({ websiteId: params.websiteId })

            if (!result) {
                throw new Error('Failed to update website')
            }

            // Prepare response
            const response = {
                success: true,
                websiteId: params.websiteId,
                backgroundColor: params.backgroundColor,
                secondaryColor: params.secondaryColor,
                updatedAt: result.updatedAt
            }

            set((state) => { state.lastWebsiteBuilderResult = response })
            return response
        } catch (error) {
            console.error('Error updating website color:', error)
            const errorResult = { success: false, error: 'Failed to update website color' }
            set((state) => { state.lastWebsiteBuilderResult = errorResult })
            return errorResult
        } finally {
            set((state) => { state.isWebsiteBuilderAction = false })
        }
    },

    updateWebsiteFont: async (params: UpdateWebsiteFontParams) => {
        set((state) => {
            state.isWebsiteBuilderAction = true
            state.lastWebsiteBuilderAction = 'updateWebsiteFont'
        })

        try {
            // Get the website builder store
            const websiteBuilder = useWebsiteBuilder.getState()

            // First load the website to update
            await websiteBuilder.getWebsite({ websiteId: params.websiteId })

            // Get the current website style
            const currentStyle = { ...websiteBuilder.websiteStyle }

            // Set the updated style with required properties
            websiteBuilder.setWebsiteStyle({
                ...currentStyle,
                fontFamily: params.fontFamily
            })

            // Update the website
            const result = await websiteBuilder.updateWebsite({ websiteId: params.websiteId })

            if (!result) {
                throw new Error('Failed to update website')
            }

            // Prepare response
            const response = {
                success: true,
                websiteId: params.websiteId,
                fontFamily: params.fontFamily,
                updatedAt: result.updatedAt
            }

            set((state) => { state.lastWebsiteBuilderResult = response })
            return response
        } catch (error) {
            console.error('Error updating website font:', error)
            const errorResult = { success: false, error: 'Failed to update website font' }
            set((state) => { state.lastWebsiteBuilderResult = errorResult })
            return errorResult
        } finally {
            set((state) => { state.isWebsiteBuilderAction = false })
        }
    },

    publishWebsite: async (params: PublishWebsiteParams) => {
        set((state) => {
            state.isWebsiteBuilderAction = true
            state.lastWebsiteBuilderAction = 'publishWebsite'
        })

        try {
            // Get the website builder store
            const websiteBuilder = useWebsiteBuilder.getState()

            // Load the website to publish
            await websiteBuilder.getWebsite({ websiteId: params.websiteId })

            // Create the payload with the PUBLISHED status
            const websiteData = websiteBuilder.websiteBuilderData

            if (!websiteData) {
                throw new Error('No website data found')
            }

            const { currentBrand } = useBrand.getState()

            // Create the payload
            const publishPayload: WebsiteBuilderDataPayload = {
                brandId: currentBrand?.id || websiteData.brandId,
                slug: websiteData.slug,
                status: WebsiteStatus.PUBLISHED,
                websiteContent: websiteBuilder.websiteContent,
                websiteStyle: websiteBuilder.websiteStyle
            }

            // Update the website with the published status
            const result = await websiteBuilder.updateWebsite({
                websiteId: params.websiteId,
                websiteBuilderPayload: publishPayload
            })

            if (!result) {
                throw new Error('Failed to publish website')
            }

            // Prepare response
            const response = {
                success: true,
                websiteId: params.websiteId,
                status: WebsiteStatus.PUBLISHED,
                publishedAt: result.updatedAt,
                websiteUrl: `https://${currentBrand?.slug}.symphony.is/${result.slug}`
            }

            set((state) => { state.lastWebsiteBuilderResult = response })
            return response
        } catch (error) {
            console.error('Error publishing website:', error)
            const errorResult = { success: false, error: 'Failed to publish website' }
            set((state) => { state.lastWebsiteBuilderResult = errorResult })
            return errorResult
        } finally {
            set((state) => { state.isWebsiteBuilderAction = false })
        }
    },

    // USES AXIOS
    // Handle file upload from Cloudinary and send the URL to the chat
    handleFileUpload: async (file: CloudinaryUploadedFileType) => {
        try {
            set((state) => { state.isUploading = true });

            // Generate chatId if this is a new chat session
            let { chatId } = get()
            if (!chatId) {
                chatId = get().generateChatId()
                get().setChatId(chatId)
            }

            const { currentBrand } = useBrand.getState();
            if (!currentBrand?.id) {
                console.error('No brand selected');
                return;
            }

            let createdAsset: any = null;
            let messageText = '';

            try {
                // STEP 1: Create uploaded asset record
                const assetResponse = await Axios.post('/uploaded-assets', {
                    cloudinaryData: file,    // Pass entire Cloudinary response
                    chatId: chatId          // Link to current chat session
                });

                createdAsset = assetResponse.data;

                // STEP 2: Create structured message text
                messageText = `I just uploaded a ${createdAsset.mediaType}${createdAsset.fileName ? ` "${createdAsset.fileName}"` : ''} (Asset ID: ${createdAsset.id})`;
            } catch (assetError) {
                console.error('Failed to create asset record:', assetError);

                // FALLBACK: Use old method with direct URL
                const fileType = file.resource_type === 'video' ? 'video' : 'image';
                messageText = `Here's my ${fileType}${file.original_filename ? ` "${file.original_filename}"` : ''}`;
            }

            // STEP 3: Add message with both text and media preview to chat
            const fileType = file.resource_type === 'video' ? 'video' : 'image';
            const newMessage: MessageItem = {
                id: Date.now(),
                text: messageText,
                createdAt: new Date().toISOString(),
                isUser: true,
                type: fileType === 'video' ? MessageType.VIDEO : MessageType.IMAGE,
                media: {
                    url: file.secure_url,
                    type: fileType,
                    thumbnailUrl: file.thumbnail_url || file.secure_url
                }
            };

            // Add the message to the chat and start the conversation
            set((state) => {
                state.messages.push(newMessage);
                state.inChatMode = true;
            });

            // Send the message to the backend (this will trigger the AI response)
            get().sendMessage(messageText)
        } catch (error) {
            console.error('Error handling file upload:', error);
            set((state) => {
                state.isUploading = false;
                state.isLoadingAIResponse = false;
            });
        } finally {
            set((state) => { state.isUploading = false });
        }
    },

    // NEW: Handle multiple file uploads from UniversalUploadWidget
    handleUniversalFileUpload: async (uploadedFiles: import('components/shareable/UniversalUploadWidget/UniversalUploadWidget').UploadedFile[]) => {
        try {
            set((state) => { state.isUploading = true });

            // Generate chatId if this is a new chat session
            let { chatId } = get()
            if (!chatId) {
                chatId = get().generateChatId()
                get().setChatId(chatId)
            }

            const { currentBrand } = useBrand.getState();
            if (!currentBrand?.id) {
                console.error('No brand selected');
                return;
            }

            // Filter only successfully uploaded files
            const successfulUploads = uploadedFiles.filter(file => file.status === 'ready' && file.uploadedAssetId);

            if (successfulUploads.length === 0) {
                console.warn('No successfully uploaded files to process');
                return;
            }

            // Create message text based on uploads
            let messageText = '';
            if (successfulUploads.length === 1) {
                const file = successfulUploads[0];
                messageText = `I just uploaded a ${file.type.split('/')[0]} "${file.name}" (Asset ID: ${file.uploadedAssetId})`;
            } else {
                const fileTypes = successfulUploads.map(f => f.type.split('/')[0]);
                const uniqueTypes = [...new Set(fileTypes)];
                messageText = `I just uploaded ${successfulUploads.length} files: ${uniqueTypes.join(', ')} (Asset IDs: ${successfulUploads.map(f => f.uploadedAssetId).join(', ')})`;
            }

            const assetsToDisplay: UploadedAssetDisplayInfo[] = successfulUploads.map(file => {
                let assetType: UploadedAssetDisplayInfo['assetType'] = 'unknown';
                if (file.type.startsWith('image/')) {
                    assetType = 'image';
                } else if (file.type.startsWith('video/')) {
                    assetType = 'video';
                } else if (file.type.startsWith('audio/')) {
                    assetType = 'audio';
                } else if (file.type === 'application/pdf' || file.type === 'text/csv' || file.type.includes('document')) {
                    assetType = 'document';
                }

                return {
                    id: String(file.uploadedAssetId || `local-${file.id}`), // Convert to string
                    assetType,
                    fileName: file.name,
                    thumbnailUrl: file.thumbnailUrl,
                    originalUrl: file.s3Url || '', // Use actual S3 URL for lightbox functionality
                    mimeType: file.type,
                };
            });

            const newMessage: MessageItem = {
                id: Date.now(),
                text: messageText, // This text already includes Asset IDs for the agent
                createdAt: new Date().toISOString(),
                isUser: true,
                type: MessageType.TEXT, // The message itself is text, but it carries asset information
                uploadedAssets: assetsToDisplay,
            };

            // Add the message to the chat and start the conversation
            set((state) => {
                state.messages.push(newMessage);
                state.inChatMode = true;
            });

            // Send the message to the backend (this will trigger the AI response)
            get().sendMessage(messageText)
        } catch (error) {
            console.error('Error handling universal file upload:', error);
            set((state) => {
                state.isUploading = false;
                state.isLoadingAIResponse = false;
            });
        } finally {
            set((state) => { state.isUploading = false });
        }
    },

    cancelOngoingRequest: () => {
        const { abortController } = get();
        if (abortController) {
            abortController.abort();
            set((state) => {
                state.isStreaming = false;
                state.currentStreamedText = '';
                state.isLoadingAIResponse = false;
                state.isBuilding = false;
                state.buildingMessage = null;
                state.abortController = null;
            });
        }
    },

    // USES AXIOS
    // Unified asset status polling method
    startAssetStatusPolling: (assetId: string, assetType: 'video' | 'image') => {
        let pollCount = 0
        const maxPolls = 40 // 40 * 3 seconds = 2 minutes max for images, longer for videos

        const { activeAssetPolls } = get()
        if (activeAssetPolls[assetId]) {
            clearInterval(activeAssetPolls[assetId])
        }

        const pollInterval = setInterval(async () => {
            try {
                pollCount++

                // Use the unified endpoint
                const response = await Axios.get(`/agents/asset-generation-status/${assetId}`)

                const status: AssetGenerationStatus = response.data

                // Update the status in pending asset generation or in existing message
                const { pendingAssetGeneration, messages } = get()

                if (pendingAssetGeneration && pendingAssetGeneration.assetId === assetId) {
                    // Still pending (not yet attached to message)
                    get().updatePendingAssetGenerationStatus(status)
                } else {
                    // Already attached to a message, update the message
                    set((state) => {
                        const messageIndex = state.messages.findIndex(msg =>
                            (msg.videoGeneration && msg.videoGeneration.videoId === assetId) ||
                            (msg.assetGeneration && msg.assetGeneration.assetId === assetId)
                        )
                        if (messageIndex !== -1) {
                            const messageWithAsset = state.messages[messageIndex]

                            // Update the asset generation status in the message
                            if (messageWithAsset.assetGeneration) {
                                state.messages[messageIndex] = {
                                    ...messageWithAsset,
                                    assetGeneration: {
                                        ...messageWithAsset.assetGeneration,
                                        status: status
                                    }
                                }
                            } else if (messageWithAsset.videoGeneration && assetType === 'video') {
                                // Update the legacy video generation with asset status
                                state.messages[messageIndex] = {
                                    ...messageWithAsset,
                                    videoGeneration: {
                                        ...messageWithAsset.videoGeneration,
                                        status: {
                                            success: status.success,
                                            videoId: assetId,
                                            status: status.status === 'draft' ? 'pending' :
                                                status.status === 'generating' ? 'generating_video' :
                                                    status.status === 'completed' ? 'completed' : 'failed',
                                            progress: status.progress,
                                            isComplete: status.status === 'completed',
                                            isFailed: status.status === 'failed',
                                            message: status.errorMessage || 'Processing...',
                                            result: status.resultMetadata ? {
                                                videoUrl: status.resultMetadata.videoUrl || '',
                                                thumbnailUrl: status.resultMetadata.thumbnailUrl || '',
                                                publicId: status.resultMetadata.publicId || '',
                                                prompt: status.resultMetadata.prompt || '',
                                                generationTime: status.resultMetadata.generationTime,
                                                metadata: status.resultMetadata.metadata
                                            } : undefined
                                        }
                                    }
                                }
                            }
                        }
                    })
                }

                // Check if complete, failed, or timed out
                if (status.status === 'completed' || status.status === 'failed' || pollCount >= maxPolls) {
                    // Clear the polling interval regardless of outcome
                    clearInterval(pollInterval)
                    set((state) => {
                        delete state.activeAssetPolls[assetId]
                    })

                    // Update the message with the final status
                    if (status.status === 'completed') {
                        // After updating the UI, notify the agent to continue its workflow
                        get().notifyAgentAssetComplete(status)
                    }
                }

            } catch (error) {
                console.error('Asset status polling failed:', error)

                // On network error, continue polling unless we've hit max attempts
                if (pollCount >= maxPolls) {
                    clearInterval(pollInterval)
                    set((state) => {
                        delete state.activeAssetPolls[assetId]
                    })

                    // Update message with timeout error
                    set((state) => {
                        const messageIndex = state.messages.findIndex(msg =>
                            (msg.videoGeneration && msg.videoGeneration.videoId === assetId) ||
                            (msg.assetGeneration && msg.assetGeneration.assetId === assetId)
                        )
                        if (messageIndex !== -1) {
                            const messageWithAsset = state.messages[messageIndex]
                            state.messages[messageIndex] = {
                                ...messageWithAsset,
                                text: `❌ ${assetType === 'video' ? 'Video' : 'Image'} generation timed out or failed to check status`,
                                videoGeneration: undefined,
                                assetGeneration: undefined
                            }
                        }
                    })
                }
            }
        }, 3000) // Poll every 3 seconds (faster than legacy video polling)

        set((state) => {
            state.activeAssetPolls[assetId] = pollInterval
        })
    },

    notifyAgentAssetComplete: async (status: AssetGenerationStatus) => {
        const { currentBrand } = useBrand.getState()

        if (!currentBrand?.id) {
            console.error('No brand selected, cannot re-engage agent.')
            return
        }

        console.log(`🔄 ${status.assetType} generation complete, re-engaging agent...`)

        // Minimal client event message - agent will find asset details in scratchpad
        const clientEventMessage = {
            id: `client-event-${Date.now()}`,
            isUser: true,
            text: `${status.assetType} generation completed`,
            createdAt: new Date().toISOString(),
            clientEvent: MaestroClientEvents.ASSET_GENERATION_COMPLETE,
            payload: {
                assetId: status.assetId,    // Just the ID - agent finds URLs in scratchpad
                assetType: status.assetType // Just the type for context
            }
        }

        // Add the client event message temporarily
        set((state) => {
            state.messages.push(clientEventMessage as any)
        })

        try {
            // Use existing sendMessage method
            await get().sendMessage('', { isClientEvent: true })
        } finally {
            // Clean up the temporary message
            set((state) => {
                const lastMessage = state.messages[state.messages.length - 1]
                if (lastMessage && lastMessage.id === clientEventMessage.id) {
                    state.messages.pop()
                }
            })
        }
    },

    // Chat ID generation method  
    generateChatId: () => {
        // Use crypto.randomUUID() if available, otherwise fallback to custom UUID generation
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID()
        }

        // Fallback UUID v4 generation for older browsers
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0
            const v = c === 'x' ? r : (r & 0x3 | 0x8)
            return v.toString(16)
        })
    },

})))

export default useMaestroChat